import React, { useState, useRef, useEffect, useMemo } from "react";
import moment from "moment";
import { ResponsiveContainer, Legend, ComposedChart, BarChart, Bar, Cell, ReferenceLine, CartesianGrid, Line, XAxis, YAxis, Tooltip, ReferenceArea } from "recharts";
import { colors_subcolors } from "containers/Next/NextReducer";
import { getRange, Button, ButtonGroup, Dropdown } from "aible";
import numeral from "numeral";
import { Popover, OverlayTrigger } from "react-bootstrap";
import Select from "react-select";
import FirstVarIcon from "images/Next-Icons/drill-in-2.svg";
import SecondVarIcon from "images/Next-Icons/drill-in-1.svg";
import StatAssocIcon from "images/Next-Icons/stats.svg";
import PopularIcon from "images/Next-Icons/mode-voting.svg";
import VotingIcon from "images/Next-Icons/model-voting.svg";
import EditNotepadIcon from "images/Next-Icons/EditNotepadIcon.svg";
import PinnedIcon from "images/Sense-Icons/pinned.svg";
import UnpinnedIcon from "images/Sense-Icons/unpinned.svg";
import PinnedHoverIcon from "images/Sense-Icons/unpin.svg";
import LeakageChartContainer from "containers/Next/Charts/LeakageChart";
import * as nextReducer from "containers/Next/NextReducer";
import * as projectReducer from "containers/Project/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as chatGPTReducer from "@/containers/ChatGPT/reducer";
import Heatmap from "components/Next/Charts/Heatmap";
import styles from "./style.module.css";
import { ChartSeriesType } from "utils/insight/types";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { compareNumeric, compareString } from "@/utils/collect";
import { useDispatch, useSelector, wrapDispatch } from "@/types/store";
import { isOptionType } from "@/utils/select";
const CHART_TICK_LENGTH = 15;
function calcImpact(data) {
    const winrateDiff = (data.y ?? 0) - (data.otherY ?? 0);
    const count = data.yCount ?? 0;
    const impact = winrateDiff * count;
    return Math.abs(impact);
}
function parseNumericLabel(label) {
    if (label === undefined || label === null) {
        return undefined;
    }
    let possibleNumber = Number(label);
    if (typeof label == "number" || !isNaN(possibleNumber)) {
        return possibleNumber;
    }
    const labelArr = label.replace("<", "").replace(">", "").split(" to ");
    const retLabel = labelArr[labelArr.length - 1];
    if (retLabel === "inf" || retLabel === "Infinity" || retLabel === "infinity") {
        return Infinity;
    }
    if (retLabel === "-inf" || retLabel === "-Infinity" || retLabel === "-infinity") {
        return -Infinity;
    }
    possibleNumber = Number(retLabel);
    if (isNaN(possibleNumber)) {
        return label;
    }
    return possibleNumber;
}
function getRGBValues(colorString) {
    const splitString = colorString.split(/(?:\(| |\)|,)+/).filter((x) => x.length > 0);
    const rgbValues = splitString.map(Number).filter((v) => !isNaN(v));
    if (rgbValues.length !== 3 && rgbValues.length !== 4) {
        throw Error(`invalid color string: ${colorString}`);
    }
    return rgbValues;
}
function convertRGBValues(rgbArr, opacity) {
    const retOpacity = opacity ?? rgbArr[3] ?? 1;
    return `rgba(${rgbArr[0]}, ${rgbArr[1]}, ${rgbArr[2]}, ${retOpacity})`;
}
function addOpacityToRGB(colorString, opacity) {
    const rgbValues = getRGBValues(colorString);
    const retString = convertRGBValues(rgbValues, opacity);
    return retString;
}
function modifyRGBValues(colorString, series, metrics, hideChartValues, showChartValue) {
    const rgbValues = getRGBValues(colorString);
    //?.filter(m => !hideChartValues[m?.label])
    const rankIndex = metrics?.metrics?.findIndex((m) => {
        return m?.series?.dataKey === series?.dataKey;
    });
    if (!(rankIndex >= 0)) {
        return "rgba(0, 0, 0, 0)";
    }
    const LOW_OPACITY = 0.5;
    const HIGH_OPACITY = 0.75;
    const oFixedRGB = convertRGBValues(rgbValues, 1);
    const subColors = colors_subcolors[oFixedRGB];
    if (subColors !== undefined && subColors?.length) {
        const retRank = Math.min(rankIndex, subColors.length - 1);
        const retColor = subColors[retRank];
        return addOpacityToRGB(retColor, retRank === subColors.length - 1 ? LOW_OPACITY : HIGH_OPACITY);
    }
    const retRank = rankIndex >= 3 ? 3 : rankIndex;
    const retOpacity = showChartValue !== undefined ? HIGH_OPACITY : rankIndex >= 3 ? LOW_OPACITY : HIGH_OPACITY;
    const nRGB = rgbValues?.map((v) => {
        const MOD_SCALAR = 25;
        const newV = Number(v) + MOD_SCALAR * retRank - 2 * MOD_SCALAR;
        return Math.max(0, Math.min(255, newV));
    });
    const retRGB = convertRGBValues(nRGB, retOpacity);
    return retRGB;
}
function checkIsHidden(label, rank, hideValuesObj, forceShow) {
    if (label === undefined || forceShow === label) {
        return false;
    }
    let isHideLine = hideValuesObj?.[label];
    if (isHideLine === undefined) {
        isHideLine = rank !== undefined && rank < 2 ? false : true;
    }
    return isHideLine;
}
const CustomizedDot = ({ cx, cy, cStroke, stroke, sIndex, isOutline, hoverLegend, isCustomTooltip, dataKey, cWidth, cHeight }) => {
    let width = cWidth !== undefined ? cWidth : hoverLegend === undefined ? 8 : hoverLegend === dataKey ? 12 : 4;
    let height = cHeight !== undefined ? cHeight : hoverLegend === undefined ? 8 : hoverLegend === dataKey ? 12 : 4;
    const retFill = cStroke ?? stroke;
    const OUTLINE_SCALE = 0.5;
    const LARGE_SCALE = 1.6;
    if (isOutline) {
        width *= OUTLINE_SCALE;
        height *= OUTLINE_SCALE;
    }
    // SQUARE
    if (sIndex === 0) {
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("rect", { width: "14", height: "14", fill: retFill })));
        // CIRCLE
    }
    else if (sIndex === 1) {
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("circle", { cx: "7", cy: "7", r: "7", fill: retFill })));
        // DIAMOND
    }
    else if (sIndex === 2) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 13 18", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M0.661182 9L6.55026 0.550262L12.4393 9L6.55026 17.4497L0.661182 9Z", fill: retFill })));
    } // STAR
    else if (sIndex === 3) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 18 17", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M9 0L11.7508 5.2138L17.5595 6.21885L13.4509 10.4462L14.2901 16.2812L9 13.68L3.70993 16.2812L4.54906 10.4462L0.440492 6.21885L6.24917 5.2138L9 0Z", fill: retFill })));
    } // TRIANGLE?
    else if (sIndex === 4) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 17 15", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M8.5 0L16.7272 14.25H0.272758L8.5 0Z", fill: retFill })));
    } // PENTAGON
    else if (sIndex === 5) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 17 15", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("g", { clipPath: "url(#clip0_35_19)" },
                React.createElement("path", { d: "M8.99994 17L4.53534 13.5L0.440441 10.7811L2.5 4.50476L3.70984 0.7188H8.99994H14.29L16 6.5538L17.5594 10.7811L13.4646 13.5L8.99994 17Z", fill: retFill })),
            React.createElement("defs", null,
                React.createElement("clipPath", { id: "clip0_35_19" },
                    React.createElement("rect", { width: "18", height: "17", fill: "white", transform: "translate(18 17) rotate(-180)" })))));
    } // BLOB
    else if (sIndex === 6) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 17 15", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M7.49997 14C9.98525 14 16.5 8 12 7C7.49993 6 9.98525 0 7.49997 0C5.01468 0 -1.5 4 2.99997 7C7.49993 10 5.01468 14 7.49997 14Z", fill: retFill })));
    } // Moon
    else if (sIndex === 7) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 17 15", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("g", { clipPath: "url(#clip0_35_26)" },
                React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9 13.7101C8.36633 13.8987 7.69495 14 7 14C3.13403 14 0 10.866 0 7C0 3.13403 3.13403 0 7 0C7.69495 0 8.36633 0.101318 9 0.289917C6.10852 1.15051 4 3.82898 4 7C4 10.171 6.10852 12.8495 9 13.7101Z", fill: retFill })),
            React.createElement("defs", null,
                React.createElement("clipPath", { id: "clip0_35_26" },
                    React.createElement("rect", { width: "9", height: "14", fill: "white" })))));
    } // Ribbon
    else if (sIndex === 8) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 17 15", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("g", { clipPath: "url(#clip0_35_30)" },
                React.createElement("path", { d: "M9.80772 0V14L5.25883 11.7633L0.709961 14V0L9.80772 0Z", fill: retFill })),
            React.createElement("defs", null,
                React.createElement("clipPath", { id: "clip0_35_30" },
                    React.createElement("rect", { width: "9.09776", height: "14", fill: "white", transform: "translate(0.709961)" })))));
    }
    // Triangle-2
    else if (sIndex === 9) {
        height *= LARGE_SCALE;
        width *= LARGE_SCALE;
        return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 17 15", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("g", { clipPath: "url(#clip0_35_15)" },
                React.createElement("path", { d: "M8.50005 15L0.272854 0.75L16.7273 0.75L8.50005 15Z", fill: retFill })),
            React.createElement("defs", null,
                React.createElement("clipPath", { id: "clip0_35_15" },
                    React.createElement("rect", { width: "17", height: "15", fill: "white", transform: "translate(17 15) rotate(-180)" })))));
    }
    return (React.createElement("svg", { x: isCustomTooltip ? 0 : cx - width / 2, y: isCustomTooltip ? 0 : cy - height / 2, width: width, height: height, viewBox: "0 0 18 17", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "M9 0L11.7508 5.2138L17.5595 6.21885L13.4509 10.4462L14.2901 16.2812L9 13.68L3.70993 16.2812L4.54906 10.4462L0.440492 6.21885L6.24917 5.2138L9 0Z", fill: retFill })));
};
const ChartIcon = ({ icon, chart }) => {
    const background = chart.color ?? "grey";
    const rHexSize = 30;
    return (React.createElement("div", { className: styles.hexagon + " " + styles.hIcon, style: {
            height: rHexSize - 3 + "px",
            width: (rHexSize - 3) * (110 / 190) + "px",
            marginLeft: "10px",
            background: background
        } },
        React.createElement("div", { className: styles.face1 + " " + styles.hIcon, style: { background: background } }),
        React.createElement("div", { className: styles.face2 + " " + styles.hIcon, style: { background: background } }),
        React.createElement("img", { src: icon, style: { transform: "scale(0.5)" } })));
};
const ChartNarrativeBody = ({ chart, narrativeMadlibs }) => {
    const { behaviorLabel, changeLabel, relativeLabel, foundLabel, impactLabel, valueLabel, relationshipLabel } = narrativeMadlibs;
    const chartType = chart.metadata.type;
    const isSecondOrder = chart.metadata.properties.length == 2;
    const bodyNarrative = chart.narrative.bodyComponents;
    if (!bodyNarrative || bodyNarrative.length === 0)
        return React.createElement(React.Fragment, null, chart.narrative.body);
    let returnText = "";
    if (chartType === "Descriptive") {
        if (isSecondOrder)
            returnText = `${behaviorLabel} ${bodyNarrative[0]} ${changeLabel} ${bodyNarrative[1]}, ${relativeLabel} ${bodyNarrative[2]}`;
        else
            returnText = chart.narrative.body;
    }
    else if (chartType === "Correlation") {
        returnText = `${bodyNarrative[0]} ${relationshipLabel}`;
    }
    else if (chartType === "ModelDriver") {
        returnText =
            `${bodyNarrative[0]} ${foundLabel} ${bodyNarrative[1]} has a ${bodyNarrative[2]} ${impactLabel} ${bodyNarrative[3]}` +
                `${bodyNarrative[4]} ${valueLabel} ${bodyNarrative[5]} ${valueLabel} ${bodyNarrative[6]}`;
    }
    return React.createElement(React.Fragment, null, returnText);
};
const ChartNarrativeBullets = ({ chart, narrativeMadlibs }) => {
    const { foundLabel, impactLabel } = narrativeMadlibs;
    const bulletsNarrative = chart.narrative.bulletsComponents ?? [];
    const bullets = chart.narrative.bullets;
    return bulletsNarrative.length > 0 && false ? (React.createElement(React.Fragment, null, bulletsNarrative.map((bullet, index) => {
        return (React.createElement("div", { className: styles.bodySection, key: index },
            React.createElement("div", { className: styles.bulletRow }, `\u2022 ${bullet[0]} ${foundLabel} ${bullet[1]} has a ${bullet[2]} ${impactLabel} ${bullet[3]}`)));
    }))) : bullets && bullets.length > 0 ? (React.createElement(React.Fragment, null, bullets.map((bullet, i) => (React.createElement("div", { key: i, className: styles.nBullet }, bullet))))) : (React.createElement(React.Fragment, null));
};
const NODES = nextReducer.HEXAGON_NODES;
function getIconFromType(type) {
    switch (type) {
        case NODES.STAT_ASSOC:
            return StatAssocIcon;
        case NODES.POPULAR:
            return PopularIcon;
        case NODES.VOTING:
            return VotingIcon;
        case NODES.SECOND_VARIABLE:
        case nextReducer.HEXAGON_NODES.DRILL_IN_CLUSTER:
            return SecondVarIcon;
        case nextReducer.HEXAGON_NODES.DRILL_IN:
        case nextReducer.HEXAGON_NODES.FIRST_VARIABLE:
        default:
            return FirstVarIcon;
    }
}
function getTextFromType(type) {
    switch (type) {
        case NODES.STAT_ASSOC:
            return "related field";
        case NODES.POPULAR:
            return "popular chart";
        case NODES.VOTING:
            return "importance as ranked by the model";
        case nextReducer.HEXAGON_NODES.DRILL_IN:
        case nextReducer.HEXAGON_NODES.DRILL_IN_CLUSTER:
            return "behavior difference";
        case NODES.SECOND_VARIABLE:
        case nextReducer.HEXAGON_NODES.FIRST_VARIABLE:
        default:
            return "";
    }
}
const ChartEditNarrativeMadlibs = ({ id, chart, chartId, editInsightLanguageModeEnabled, updateEditInsightLanguageModeEnabled, customDescription, updateCustomDescription, stateNodeChartType, updateStateNodeChartType, isSmall }) => {
    const dispatch = useDispatch();
    const updateNarrativeMadlib = wrapDispatch(dispatch, nextReducer.updateNarrativeMadlib);
    const updateNodeDescription = wrapDispatch(dispatch, nextReducer.updateNodeDescription);
    const updateNodeNarrativeMadlib = wrapDispatch(dispatch, nextReducer.updateNodeNarrativeMadlib);
    const updateNodeChartType = wrapDispatch(dispatch, nextReducer.updateNodeChartType);
    const narrativeMadlibs = useSelector((state) => isSmall ? {} : nextReducer.getNarrativeMadlibs(state, id));
    const nodeDescriptions = useSelector((state) => isSmall ? {} : nextReducer.getNodeDescriptions(state, id));
    const nodeNarrativeMadlibs = useSelector((state) => isSmall ? {} : nextReducer.getNodeNarrativeMadlibs(state, id, chartId) || {});
    const nodeChartType = useSelector((state) => nextReducer.getNodeChartTypes(state, id)[chartId] ?? "");
    const [stateMadlibs, updateStateMadlibs] = useState(narrativeMadlibs);
    const [newMadlibs, updateNewMadlibs] = useState(narrativeMadlibs);
    const [updateSimilarInsights, updateUpdateSimilarInsights] = useState(true);
    const bodyNarrative = chart.narrative.bodyComponents ?? [];
    const bulletsNarrative = chart.narrative.bulletsComponents ?? [];
    const chartType = chart.metadata.type;
    const isSecondOrder = chart.metadata.properties.length == 2;
    const constructMadlibs = () => {
        const constructorMadlibs = {
            ...narrativeMadlibs,
            ...nodeNarrativeMadlibs,
            ...newMadlibs
        };
        updateStateMadlibs(constructorMadlibs);
    };
    useEffect(() => {
        constructMadlibs();
    }, []);
    const handleExit = () => {
        constructMadlibs();
        updateEditInsightLanguageModeEnabled(!editInsightLanguageModeEnabled);
        updateNewMadlibs({
            behaviorLabel: "",
            changeLabel: "",
            foundLabel: "",
            impactLabel: "",
            relationshipLabel: "",
            relativeLabel: "",
            valueLabel: ""
        });
    };
    const updateMadlibInput = (parameter, value) => {
        const currentNewMadlibs = { ...newMadlibs };
        currentNewMadlibs[parameter] = value;
        updateStateMadlibs({ ...stateMadlibs, [parameter]: value });
        updateNewMadlibs(currentNewMadlibs);
    };
    const updateMadlibs = () => {
        if (Object.keys(newMadlibs).length > 0) {
            const newMadlibPayload = {
                ...narrativeMadlibs,
                ...newMadlibs
            };
            if (updateSimilarInsights) {
                updateNarrativeMadlib(id, newMadlibPayload);
            }
            else {
                updateNodeNarrativeMadlib(id, chartId, newMadlibs);
            }
        }
        //handle update description
        if (nodeDescriptions?.[chartId] !== customDescription)
            updateNodeDescription(id, chartId, customDescription);
        if (nodeChartType !== stateNodeChartType)
            updateNodeChartType(id, chartId, stateNodeChartType);
        handleExit();
    };
    const renderBullets = () => {
        return bulletsNarrative.map((bullet, index) => {
            if (chartType === "Descriptive") {
                if (index === 0) {
                    return (React.createElement("div", { className: styles.bulletRow, key: index },
                        React.createElement("div", { className: styles.madlibsItem }, `\u2022`),
                        React.createElement("div", { className: styles.madlibsItem }, bullet[0]),
                        React.createElement("input", { className: styles.madlibsItemInput, value: stateMadlibs.foundLabel, onChange: (e) => updateMadlibInput("foundLabel", e.target.value), style: { width: "150px" } }),
                        React.createElement("div", { className: styles.madlibsItem }, bullet[1]),
                        React.createElement("div", { className: styles.madlibsItem }, "has a " + bullet[2]),
                        React.createElement("input", { className: styles.madlibsItemInput, value: stateMadlibs.impactLabel, onChange: (e) => updateMadlibInput("impactLabel", e.target.value), style: { width: "90px" } }),
                        React.createElement("div", { className: styles.madlibsItem }, bullet[3])));
                }
                return (React.createElement("div", { className: styles.bodySection, key: index },
                    React.createElement("div", { className: styles.bulletRow }, `\u2022 ${bullet[0]} ${stateMadlibs.foundLabel} ${bullet[1]} has a ${bullet[2]} ${stateMadlibs.impactLabel} ${bullet[3]}`)));
            }
            return null;
        });
    };
    const renderDescriptiveNarrativeBodyEdit = () => {
        return isSecondOrder ? (React.createElement("div", { className: styles.bodySection },
            React.createElement("input", { className: styles.madlibsItemInput, onChange: (e) => updateMadlibInput("behaviorLabel", e.target.value), value: stateMadlibs.behaviorLabel, style: { width: "100x" } }),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[0]),
            React.createElement("input", { className: styles.madlibsItemInput, onChange: (e) => updateMadlibInput("changeLabel", e.target.value), value: stateMadlibs.changeLabel, style: { width: "125px" } }),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[1] + ","),
            React.createElement("input", { className: styles.madlibsItemInput, onChange: (e) => updateMadlibInput("relativeLabel", e.target.value), value: stateMadlibs.relativeLabel, style: { width: "225px" } }),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[1]))) : (React.createElement("div", { className: styles.bodySection }, chart?.narrative?.body));
    };
    const renderCorrelationEdit = () => {
        return (React.createElement("div", { className: styles.bodySection },
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[0]),
            React.createElement("input", { className: styles.madlibsItemInput, onChange: (e) => updateMadlibInput("relationshipLabel", e.target.value), value: stateMadlibs.relationshipLabel, style: { width: "225px" } })));
    };
    const renderModelDriverEdit = () => {
        return (React.createElement("div", { className: styles.bulletRow },
            React.createElement("div", { className: styles.madlibsItem }, `\u2022`),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[0]),
            React.createElement("input", { className: styles.madlibsItemInput, value: stateMadlibs.foundLabel, onChange: (e) => updateMadlibInput("foundLabel", e.target.value), style: { width: "150px" } }),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[1]),
            React.createElement("div", { className: styles.madlibsItem }, "has a " + bodyNarrative[2]),
            React.createElement("input", { className: styles.madlibsItemInput, value: stateMadlibs.impactLabel, onChange: (e) => updateMadlibInput("impactLabel", e.target.value), style: { width: "90px" } }),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[3]),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[4]),
            React.createElement("input", { className: styles.madlibsItemInput, value: stateMadlibs.valueLabel, onChange: (e) => updateMadlibInput("valueLabel", e.target.value), style: { width: "90px" } }),
            React.createElement("div", { className: styles.madlibsItem }, bodyNarrative[5]),
            React.createElement("div", { className: styles.madlibsItem }, stateMadlibs.valueLabel + " " + bodyNarrative[6])));
    };
    const chartOptions = [ChartSeriesType.Bar, ChartSeriesType.Line].map((chartType) => ({
        value: chartType,
        label: chartType
    }));
    return (React.createElement("div", { className: styles.editMadlibsContainer },
        React.createElement("div", { className: styles.descriptionSection, style: { maxWidth: "300px" } },
            React.createElement("strong", null, "Chart Type (Chart specific)"),
            React.createElement(Dropdown, { options: chartOptions, selected: stateNodeChartType || chartOptions[0].value, onChange: (option) => {
                    updateStateNodeChartType(option.value);
                } })),
        React.createElement("div", { className: styles.descriptionSection },
            React.createElement("strong", null, "Add Description (Chart specific)"),
            React.createElement("textarea", { className: styles.descriptionTextarea, value: customDescription, onChange: (e) => updateCustomDescription(e.target.value) })),
        React.createElement("strong", null, "Update Insight Language"),
        React.createElement("div", { className: styles.madlibsSection },
            bodyNarrative && chartType === "Descriptive"
                ? renderDescriptiveNarrativeBodyEdit()
                : chartType === "Correlation"
                    ? renderCorrelationEdit()
                    : chartType === "ModelDriver"
                        ? renderModelDriverEdit()
                        : chart?.narrative?.body,
            bulletsNarrative?.length > 0 && React.createElement("div", null, renderBullets())),
        React.createElement("div", { className: styles.actionsRow },
            React.createElement(Button, { type: "secondary", onClick: () => updateMadlibs() }, "Update"),
            React.createElement("a", { style: { color: "#7E69D7", marginLeft: "20px" }, onClick: () => handleExit() }, "Cancel Update")),
        React.createElement("div", { className: styles.updateAllSection },
            React.createElement("input", { type: "checkbox", style: { marginRight: "10px" }, checked: updateSimilarInsights, onChange: () => updateUpdateSimilarInsights(!updateSimilarInsights) }),
            "Update for all similar insights (this will update the agent template)")));
};
const ChartTinyScore = ({ node, id, outcome, datasetId }) => {
    const nText = node?.chart?.scoreRank === 0
        ? "strongest"
        : node?.chart?.scoreRank === 1
            ? "second largest"
            : node?.chart?.scoreRank === 2
                ? "third largest"
                : node?.chart?.scoreRank === 3
                    ? "fourth largest"
                    : node?.chart?.scoreRank === 4
                        ? "fifth largest"
                        : node?.chart?.scoreRank === 5
                            ? "sixth largest"
                            : "weakeast";
    return (React.createElement("div", { className: styles.tinyMuscleScore },
        React.createElement(LeakageChartContainer, { id: id, datasetId: datasetId, height: 110, width: 110, innerRadius: 13, node: node, isTiny: true, hideChords: true }),
        React.createElement("div", { className: styles.tinyScore }, (node?.chart?.scoreRank ?? 0) + 1),
        React.createElement("div", { className: styles.tinyScoreText },
            React.createElement("span", { style: { fontWeight: "bold" } }, node?.fields?.[0]?.label),
            React.createElement("span", { style: { marginLeft: "3px" } }, "has the " + nText + " relationship with"),
            React.createElement("span", { style: { fontWeight: "bold", marginLeft: "3px" } }, outcome))));
};
const ChartRelate = ({ node, chart, highlightProps, defaultIsShowMore, updateHoverLinkIndex }) => {
    const [isShowMore, updateIsShowMore] = useState(defaultIsShowMore ?? false);
    return node?.chart?.narrative?.topText === undefined ||
        (node?.chart?.metadata?.properties && node?.chart?.metadata?.properties.length == 0) ? (React.createElement("div", null)) : (React.createElement("div", { className: styles.relateInfo },
        React.createElement("div", { className: styles.relateText },
            React.createElement("div", { onMouseEnter: () => updateHoverLinkIndex(chart?.narrative?.summaryValue), onMouseLeave: () => updateHoverLinkIndex(false), style: highlightProps === chart?.narrative?.summaryValue ? { fontWeight: "bold" } : {} }, node?.chart?.narrative?.topText + " "),
            isShowMore === true ? (React.createElement("span", null,
                React.createElement("span", null, "The next top " + chart?.narrative?.conditionsLabel + " behavior differences are "),
                (chart.narrative.expandText ?? []).map((eText, i) => (React.createElement("span", { onMouseEnter: () => updateHoverLinkIndex(eText.column), onMouseLeave: () => updateHoverLinkIndex(false), key: "diffs_" + i, style: highlightProps === eText.column ? { fontWeight: "bold" } : {} }, eText.text))),
                React.createElement("a", { style: { marginLeft: "5px" }, onClick: () => updateIsShowMore(false) }, "Show Less"))) : chart?.narrative?.expandText ? (React.createElement("a", { style: { marginLeft: "5px" }, onClick: () => updateIsShowMore(true) }, "Show More")) : (React.createElement(React.Fragment, null)))));
};
function getIsVisible(ele, container) {
    const { bottom, height, top } = ele.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    return top <= containerRect.top ? containerRect.top - top <= height : bottom - containerRect.bottom <= height;
}
const ChartNextActions = ({ reportId, node, updateIsExpandChart, isExpandChart, hidePin }) => {
    const dispatch = useDispatch();
    const removeOrAddNode = wrapDispatch(dispatch, nextReducer.removeOrAddNode);
    const isInReport = useSelector((state) => nextReducer.getIsInReport(state, reportId, node?.chart));
    return (React.createElement("div", { className: styles.nextActions },
        React.createElement(ButtonGroup, null,
            hidePin ? (React.createElement(React.Fragment, null)) : isInReport ? (React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top" },
                    React.createElement("div", null, "Unpin Chart")) },
                React.createElement(Button, { onClick: () => removeOrAddNode(node?.chart?.chart), style: { padding: "3px" }, className: styles.pinButton },
                    React.createElement("img", { src: PinnedHoverIcon, className: styles.pinHover }),
                    React.createElement("img", { src: PinnedIcon, className: styles.pinNoHover })))) : (React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top" },
                    React.createElement("div", null, "Add Chart")) },
                React.createElement(Button, { onClick: () => removeOrAddNode(node?.chart?.chart), style: { padding: "3px" } },
                    React.createElement("img", { src: UnpinnedIcon, className: styles.fPin })))),
            React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top" },
                    React.createElement("div", null, isExpandChart !== undefined ? "Hide Fullscreen" : "Show Fullscreen")) },
                React.createElement(Button, { onClick: () => updateIsExpandChart?.(isExpandChart !== undefined ? undefined : node?.chart?.chart), style: { padding: "3px" } }, isExpandChart !== undefined ? "Collapse" : "Expand")))));
};
function isGroupedNarrative(x) {
    return x !== undefined && Array.isArray(x.metrics);
}
const getComparisonNarrative = (insight, comparisonOptions) => {
    if (!comparisonOptions) {
        return undefined;
    }
    const chart = insight.chart;
    const chartData = chart.data.filter((d) => {
        return (nextReducer.getIsReferenceDate(undefined, undefined, d?.name, comparisonOptions) ||
            nextReducer.getIsCurrentDate(undefined, undefined, d?.name, comparisonOptions));
    });
    let comparisonNarrative = undefined;
    if (comparisonOptions) {
        if (insight.metadata.properties.length == 1) {
            const metrics = chart?.series?.map((s) => {
                const modifierIndex = s?.modifierIndex;
                const dataKeyChart = {
                    name: "name_" + modifierIndex,
                    label: "label_" + modifierIndex,
                    otherLabel: "otherLabel_" + modifierIndex,
                    y: "y_" + modifierIndex,
                    otherY: "otherY_" + modifierIndex,
                    yCount: "yCount_" + modifierIndex,
                    otherYCount: "otherYCount_" + modifierIndex,
                    summary: "summary_" + modifierIndex,
                    otherSummary: "otherSummary_" + modifierIndex
                };
                const currMetrics = nextReducer.getMetricsFromChartData(chartData, comparisonOptions, dataKeyChart);
                return {
                    metrics: currMetrics,
                    series: s,
                    rank: 0
                };
            });
            metrics.sort((m1, m2) => {
                return Math.abs(m2?.metrics?.difference ?? 0) - Math.abs(m1?.metrics?.difference ?? 0);
            });
            metrics.forEach((m, i) => {
                metrics[i].rank = i;
            });
            comparisonNarrative = {
                metrics: metrics
            };
        }
        else {
            const metrics = nextReducer.getMetricsFromChartData(chartData, comparisonOptions);
            const winsChangeActual = metrics?.newWins - metrics?.previousWins;
            const popChangeActual = metrics?.newPursued - metrics?.previousPursued;
            comparisonNarrative = {
                metrics: metrics,
                difference: metrics?.difference,
                countChange: metrics?.countChange,
                totalCountChange: metrics?.totalCountChange,
                newWins: metrics?.newWins,
                newPursued: metrics?.newPursued,
                previousWins: metrics?.previousWins,
                previousPursued: metrics?.previousPursued,
                winsChange: (winsChangeActual * 100) / (metrics?.previousWins || 1),
                winsChangeActual: winsChangeActual,
                popChange: (popChangeActual * 100) / (metrics?.previousPursued || 1),
                popChangeActual: popChangeActual
            };
        }
        comparisonNarrative.currentStart = comparisonOptions.currentStart;
        comparisonNarrative.currentEnd = comparisonOptions.currentEnd;
        comparisonNarrative.referenceStart = comparisonOptions.referenceStart;
        comparisonNarrative.referenceEnd = comparisonOptions.referenceEnd;
    }
    return comparisonNarrative;
};
const ChartNarrative = ({ id, insight, node, highlightProps, editInsightLanguageModeEnabled, updateEditInsightLanguageModeEnabled, updateHoverLinkIndex, stateNodeChartType, updateStateNodeChartType, isExploreTask, isChatGPT, isSmall, isShowMore }) => {
    const chartId = insight.metadata.type + "-" + insight.narrative.title;
    const nodeDescriptions = useSelector((state) => isSmall ? {} : nextReducer.getNodeDescriptions(state, id));
    const [customDescription, updateCustomDescription] = useState(nodeDescriptions?.[chartId] ?? "");
    const narrativeMadlibs = useSelector((state) => isSmall ? {} : nextReducer.getNarrativeMadlibs(state, id));
    return editInsightLanguageModeEnabled ? (React.createElement(ChartEditNarrativeMadlibs, { id: id, chart: insight, chartId: chartId, customDescription: customDescription, updateCustomDescription: updateCustomDescription, stateNodeChartType: stateNodeChartType, updateStateNodeChartType: updateStateNodeChartType, editInsightLanguageModeEnabled: editInsightLanguageModeEnabled, updateEditInsightLanguageModeEnabled: updateEditInsightLanguageModeEnabled })) : (React.createElement("div", { className: styles.narrativeHolder },
        React.createElement(ChartRelate, { chart: insight, node: node, highlightProps: highlightProps, defaultIsShowMore: isShowMore, updateHoverLinkIndex: updateHoverLinkIndex }),
        customDescription.length > 0 && (React.createElement("div", { style: { marginBottom: "10px" } },
            React.createElement("div", null, customDescription))),
        ((insight?.metadata?.properties && insight?.metadata?.properties.length === 0) ||
            node?.chart?.narrative?.topText === undefined) && (React.createElement(ChartNarrativeBody, { chart: insight, narrativeMadlibs: narrativeMadlibs })),
        (insight?.narrative?.bullets ?? []).length > 0 && (React.createElement(ChartNarrativeBullets, { chart: insight, narrativeMadlibs: narrativeMadlibs })),
        isExploreTask !== true && !isChatGPT && (React.createElement("a", { className: styles.editLink, onClick: () => updateEditInsightLanguageModeEnabled(!editInsightLanguageModeEnabled) },
            React.createElement("img", { style: { marginRight: "10px" }, src: EditNotepadIcon }),
            "Edit Insight Details"))));
};
function isInsight(x) {
    const ei = x;
    return ei.chart !== undefined && ei.chart.data !== undefined && ei.chart.series !== undefined;
}
const getId = (props) => (props && props.id ? props.id : props.datasetId ? props.datasetId : "new");
const getChartData = ({ chart, shouldSortData, reportChartSort, comparisonOptions, onlyShowData, isNumeric, isContinuous, continuousGraphDisplayType }) => {
    const filteredRawChartData = onlyShowData && onlyShowData?.length > 0
        ? chart.data.filter((data) => onlyShowData.includes(data?.name))
        : chart.data;
    if (comparisonOptions !== undefined) {
        const currentEnd = moment(comparisonOptions.currentEnd);
        const referenceState = moment(comparisonOptions.referenceStart);
        return filteredRawChartData
            .map((d) => [moment(d.name), d])
            .filter((x) => {
            return x[0].isValid() && x[0].isSameOrAfter(referenceState) && x[0].isSameOrBefore(currentEnd);
        })
            .sort((a, b) => {
            if (a[0].isBefore(b[0])) {
                return -1;
            }
            else if (a[0].isAfter(b[0])) {
                return 1;
            }
            return 0;
        })
            .map((x) => x[1]);
    }
    const rawChartData = shouldSortData
        ? filteredRawChartData.toSorted((a, b) => {
            if (reportChartSort === undefined || reportChartSort === "IMPACT") {
                const aVal = calcImpact(a);
                const bVal = calcImpact(a);
                return bVal - aVal;
            }
            else if (reportChartSort === "OUTCOME") {
                const aVal = a?.y || 0;
                const bVal = b?.y || 0;
                if (aVal < bVal) {
                    return 1;
                }
                else if (aVal > bVal) {
                    return -1;
                }
                else {
                    return 0;
                }
            }
            else if (reportChartSort === "COUNT") {
                const aCount = a?.summary?.getTotalN() || 0;
                const bCount = b?.summary?.getTotalN() || 0;
                if (aCount < bCount) {
                    return 1;
                }
                else if (aCount > bCount) {
                    return -1;
                }
            }
            let aLabel = chart.xAxis.dataKey !== undefined ? a[chart.xAxis.dataKey] : "";
            let bLabel = chart.xAxis.dataKey !== undefined ? b[chart.xAxis.dataKey] : "";
            if (!isNaN(Number(aLabel)) && !isNaN(Number(bLabel))) {
                return compareNumeric(Number(aLabel), Number(bLabel));
            }
            else if (isNumeric) {
                aLabel = parseNumericLabel(aLabel);
                bLabel = parseNumericLabel(bLabel);
            }
            if (typeof aLabel == "number" && typeof bLabel == "number") {
                return compareNumeric(aLabel, bLabel);
            }
            return compareString(String(aLabel), String(aLabel));
        })
        : filteredRawChartData;
    return isContinuous && continuousGraphDisplayType === "TOTAL"
        ? rawChartData.map((d) => {
            const retData = { ...d };
            if (d.y !== undefined) {
                retData.yTotal = d.summary?.sum;
            }
            if (d.otherY !== undefined) {
                retData.otherYTotal = d.otherSummary?.sum;
            }
            return retData;
        })
        : rawChartData.map((d) => (d.y === undefined ? { ...d, y: 0 } : d));
};
const NextChart = (props) => {
    const { id, isReport, useReportIdForChatGPTReducer, isSmall, style, showLeakage, showTooltip, tooltipPosition, reportChartSort, isHexagon, isTableauMode, isGoogleMode, isExploreTask, isListChart, highlightProps, isReportExpand, hideLegend, showAxis, showTitle, hideTitle, isDashboard, isInfographic, isExpand, isHideNarrative, narrativeText, isChatGPT, isComparisonDashboard, isComparisonShowAll, yDomainOverride, allowedSecondOrderValuesOrdered, onlyShowData, isShowMore, insight, isOpenShell } = props;
    const dispatch = useDispatch();
    const rawReportId = props?.reportId ?? props?.id;
    const reportId = rawReportId === "new" ? rawReportId : Number(rawReportId);
    const reportDatasetId = useSelector((state) => nextReducer.getDatasetId(state, reportId));
    const datasetId = props?.datasetId ?? props?.dataset?.id ?? reportDatasetId;
    const projectId = useSelector((state) => props.projectId ?? nextReducer.getProjectId(state, id));
    // Get the fields for constructing nodes
    const nFields = useSelector((state) => (props.node ? undefined : nextReducer.getFieldsFromChart(state, reportId, insight)), () => true // this is derived state so we can rely on other selectors
    );
    const node = props.node ??
        {
            chart: insight,
            fields: nFields //This is needed for the leakage chart
        };
    const nodeChartId = insight?.metadata?.type + "-" + insight?.narrative?.title;
    const nodeChartType = useSelector((state) => (nextReducer.getNodeChartTypes(state, id) ?? {})[nodeChartId] ?? "");
    const isDifferenceTypeInsight = insight?.metadata?.type === "Difference";
    const comparisonOptions = useSelector((state) => nextReducer.getComparisonOptions(state, id) ??
        (isDifferenceTypeInsight ? homeReducer.getDatasetSavedComparisonOptions(state, datasetId) : undefined));
    const comparisonChartOptions = useSelector((state) => nextReducer.getComparisonChartOptions(state, id));
    const dataset = useSelector((state) => homeReducer.getDataset(state, datasetId));
    const outcome = dataset?.outcome;
    const isContinuous = outcome !== undefined && dataset?.fields?.[outcome]?.type === "number";
    const project = useSelector((state) => projectReducer.getProject(state, projectId));
    const runId = useSelector((state) => projectReducer.getRunId(state, projectId));
    const comparisonDashboardSortValue = useSelector((state) => nextReducer.getComparisonDashBoardSort(state, id)?.value);
    const datasetSuccessLabel = useSelector((state) => nextReducer.getDatasetSuccess(state, reportId)?.label);
    const continuousGraphDisplayType = useSelector((state) => nextReducer.getContinuousGraphDisplayType(state, reportId));
    const isHeatmapGlobalScale = useSelector((state) => nextReducer.getIsHeatmapGlobalScale(state, reportId));
    const chatGPTReducerReportId = useReportIdForChatGPTReducer ? props?.reportId : undefined;
    const hideChangeInBehavior = useSelector((state) => chatGPTReducer.getHideChangeInBehavior(state, undefined, undefined, chatGPTReducerReportId));
    const hideChangeInPopulation = useSelector((state) => chatGPTReducer.getHideChangeInPopulation(state, undefined, undefined, chatGPTReducerReportId));
    const hideChangeInOutcome = useSelector((state) => chatGPTReducer.getHideChangeInOutcome(state, undefined, undefined, chatGPTReducerReportId));
    const hideChangeInTotalPopulation = useSelector((state) => chatGPTReducer.getHideChangeInTotalPopulation(state, undefined, undefined, chatGPTReducerReportId));
    const renameBehavior = useSelector((state) => chatGPTReducer.getRenameBehavior(state, undefined, undefined, chatGPTReducerReportId));
    const renameOutcome = useSelector((state) => chatGPTReducer.getRenameOutcome(state, undefined, undefined, chatGPTReducerReportId));
    const renamePopulation = useSelector((state) => chatGPTReducer.getRenamePopulation(state, undefined, undefined, chatGPTReducerReportId));
    const renameTotalPopulation = useSelector((state) => chatGPTReducer.getRenameTotalPopulation(state, undefined, undefined, chatGPTReducerReportId));
    const updateDrilldownValue = wrapDispatch(dispatch, projectReducer.updateDrilldownValue);
    const [selected, updateSelected] = useState({});
    const [hovered, updateHovered] = useState();
    const [editInsightLanguageModeEnabled, updateEditInsightLanguageModeEnabled] = useState(false);
    const [hideChartValues, updateHideChartValues] = useState({});
    const [showChartValue, updateShowChartValue] = useState();
    const [isHighlight, updateIsHighlight] = useState();
    const [hoverLinkIndex, updateHoverLinkIndex] = useState();
    const [stateNodeChartType, updateStateNodeChartType] = useState(nodeChartType);
    const comparisonNarrative = useMemo(() => getComparisonNarrative(insight, comparisonOptions), [nodeChartId, comparisonOptions]);
    let chart = insight.chart;
    if (allowedSecondOrderValuesOrdered && allowedSecondOrderValuesOrdered?.length > 0) {
        const allowedData = allowedSecondOrderValuesOrdered
            .map((allowedValue) => {
            return chart?.data?.find((data) => data?.name === allowedValue);
        })
            .filter((data) => data !== undefined);
        const retChart = {
            ...chart,
            data: allowedData
        };
        chart = retChart;
    }
    const color = insight.color ?? "grey";
    const xAxisDataKey = chart.xAxis.dataKey;
    const xAxisMin = xAxisDataKey === undefined
        ? undefined
        : Math.min(...chart.data.map((d) => d[xAxisDataKey]).filter((x) => typeof x == "number"));
    const rawSeries = comparisonOptions && !isSmall ? chart.series.filter((s) => s.dataKey !== "otherY") : chart.series;
    const yPoints = rawSeries
        .flatMap((series) => chart.data.map((d) => {
        // Filter out Missing values from range calculation
        if (comparisonOptions && d?.name === "{Missing}") {
            return undefined;
        }
        return d[series.dataKey];
    }))
        .filter((x) => x !== undefined)
        .filter((x) => typeof x == "number");
    const yAxisMax = Math.max(...yPoints);
    const yAxisMin = Math.min(...yPoints);
    const getHandleSeriesClick = (i) => () => updateSelected({
        [i]: !selected[i]
    });
    const getHandleSeriesHover = (i) => () => updateHovered(i);
    const isNumeric = insight.isNumeric ?? false;
    const chartColumn = insight?.narrative?.columnLabel;
    let shouldSortData = dataset?.fields?.[chartColumn]?.type !== "date";
    if (isNumeric) {
        shouldSortData = reportChartSort !== undefined && reportChartSort !== "ALPHABETIC";
    }
    const chartType = (stateNodeChartType?.length ?? 0) > 0 ? stateNodeChartType : chart?.series?.length > 0 && chart.series[0].type;
    const currentSegment = runId !== undefined ? project.segments.filter((segment) => segment?.job?.job_id === runId)?.[0] : undefined;
    const chartData = getChartData({
        chart,
        shouldSortData,
        reportChartSort,
        comparisonOptions,
        onlyShowData,
        isNumeric,
        isContinuous,
        continuousGraphDisplayType
    });
    const longestName = chartData.reduce((a, b) => Math.max(a, chart.xAxis.dataKey !== undefined && b[chart.xAxis.dataKey] !== undefined
        ? (b[chart.xAxis.dataKey] ?? "").toString().length
        : 0), 0);
    const retSeries = comparisonOptions
        ? [...chart.series].reverse()
        : isContinuous && continuousGraphDisplayType === "TOTAL"
            ? chart.series.map((s) => {
                const retS = { ...s };
                switch (retS.dataKey) {
                    case "y":
                        retS.dataKey = "yTotal";
                        break;
                    case "otherY":
                        retS.dataKey = "otherYTotal";
                        break;
                }
                return retS;
            })
            : chart.series;
    const successLabel = `${outcome}${datasetSuccessLabel ? ": " + datasetSuccessLabel : ""}`;
    const showAll = isReportExpand || chartData.length < 15;
    const hasNarrativeBullets = insight?.narrative?.bullets && insight?.narrative?.bullets.length > 0;
    let yDomain = [0, 100];
    const comparisonReferenceStart = comparisonOptions?.referenceStart;
    const comparisonReferenceEnd = comparisonOptions?.referenceEnd;
    const comparisonCurrentStart = comparisonOptions?.currentStart;
    const comparisonCurrentEnd = comparisonOptions?.currentEnd;
    let otherYDomain = undefined;
    if (comparisonOptions) {
        if (!isSmall) {
            const otherYPoints = chart.series
                .filter((s) => s.dataKey === "otherY")
                .flatMap((series) => chart.data.map((d) => {
                // Filter out Missing values from range calculation
                if (d?.name === "{Missing}") {
                    return undefined;
                }
                return d[series.dataKey];
            }))
                .filter((x) => x !== undefined);
            const otherYMetrics = {
                otherYPoints: otherYPoints,
                otherYAxisMin: Math.min(...otherYPoints.filter((x) => typeof x == "number")),
                otherYAxisMax: Math.max(...otherYPoints.filter((x) => typeof x == "number"))
            };
            const SIMPLE_SCALING = false;
            const yMin = yAxisMin;
            const yMax = yAxisMax;
            const otherYMin = otherYMetrics.otherYAxisMin;
            const otherYMax = otherYMetrics.otherYAxisMax;
            const chartGranularity = comparisonChartOptions.granularity;
            const normalizeRange = comparisonChartOptions.normalize;
            if (SIMPLE_SCALING) {
                yDomain = getRange(yMin, yMax, true, 1);
                otherYDomain = getRange(otherYMin, otherYMax, true, 1);
                yDomain[0] = Math.max(yDomain[0], 0);
                yDomain[1] = Math.min(yDomain[1], 100);
                otherYDomain[0] = Math.max(otherYDomain[0], 0);
                otherYDomain[1] = Math.min(otherYDomain[1], 100);
            }
            else {
                // Taking ranges in granularity of .1 increments
                const actualYMin = Math.max(0, Math.floor(yMin / chartGranularity) * chartGranularity);
                const actualYMax = Math.min(100, Math.ceil(yMax / chartGranularity) * chartGranularity);
                const actualOtherYMin = Math.max(0, Math.floor(otherYMin / chartGranularity) * chartGranularity);
                const actualOtherYMax = Math.min(100, Math.ceil(otherYMax / chartGranularity) * chartGranularity);
                let normalizedYMax = actualYMax;
                let normalizedOtherYMax = actualOtherYMax;
                // This keeps the difference in the scales the same
                if (normalizeRange) {
                    const yDiff = actualYMax - actualYMin;
                    const otherYDiff = actualOtherYMax - actualOtherYMin;
                    if (yDiff > otherYDiff) {
                        normalizedOtherYMax = actualOtherYMax + (yDiff - otherYDiff);
                    }
                    else if (otherYDiff > yDiff) {
                        normalizedYMax = actualYMax + (otherYDiff - yDiff);
                    }
                }
                // Use new range
                yDomain = [actualYMin, normalizedYMax];
                otherYDomain = [actualOtherYMin, normalizedOtherYMax];
            }
        }
        // Make sure there is always an entry for the dates
        const COMPARISON_DATES = [
            comparisonReferenceStart,
            comparisonReferenceEnd,
            comparisonCurrentStart,
            comparisonCurrentEnd
        ];
        COMPARISON_DATES.forEach((cDate) => {
            if (!chartData.find((d) => d.name === cDate)) {
                let insertionIndex = undefined;
                for (let i = 0; i < chartData.length; i++) {
                    if (moment(chartData[i].name).isAfter(cDate)) {
                        insertionIndex = i;
                        break;
                    }
                }
                if (insertionIndex === undefined) {
                    chartData.push({ name: cDate });
                }
                else {
                    chartData.splice(insertionIndex, 0, { name: cDate });
                }
            }
        });
        // Add a spacer at the end
        if (chartData[chartData?.length - 1].name === comparisonCurrentEnd &&
            comparisonCurrentStart !== comparisonCurrentEnd) {
            chartData.push({ name: " " });
        }
    }
    else {
        if (!isNaN(yAxisMax) && !isNaN(yAxisMin)) {
            yDomain = getRange(Math.min(0, yAxisMin), yAxisMax, true);
        }
        if (!isNaN(yAxisMin) && yAxisMin >= 0) {
            yDomain[0] = 0;
        }
    }
    // Update domain calculations to return an array of either "auto" or functions that return values. This fixes Recharts
    // doing domain expansions on values and treats the domains as more concrete when passed a function than how they're treated when passed as values.
    let fixDomain1 = ["auto", "auto"];
    let fixDomain2 = ["auto", "auto"];
    if (hideChangeInBehavior === true) {
        const outcomeData = chartData?.map((cd) => cd?.summary?.sum).filter((cd) => typeof cd == "number");
        fixDomain1 = [() => Math.min.apply(null, outcomeData), () => Math.max.apply(null, outcomeData)];
        if (comparisonChartOptions) {
            const chartGranularity = comparisonChartOptions.granularity;
            const granularMin = Math.floor(Math.min.apply(null, outcomeData) / chartGranularity) * chartGranularity;
            const granularMax = Math.ceil(Math.max.apply(null, outcomeData) / chartGranularity) * chartGranularity;
            fixDomain1 = [() => granularMin, () => granularMax];
            console.log("Fix domain 1 is: ", fixDomain1);
        }
    }
    if (hideChangeInPopulation === true) {
        const popData = chartData?.map((cd) => cd?.yCount).filter((cd) => typeof cd == "number");
        fixDomain2 = [() => Math.min.apply(null, popData), () => Math.max.apply(null, popData)];
        if (comparisonChartOptions) {
            const chartGranularity = comparisonChartOptions.granularity;
            const granularMin = Math.floor(Math.min.apply(null, popData) / chartGranularity) * chartGranularity;
            const granularMax = Math.ceil(Math.max.apply(null, popData) / chartGranularity) * chartGranularity;
            fixDomain2 = [() => granularMin, () => granularMax];
            console.log("Fix domain 2 is: ", fixDomain2);
        }
    }
    if (insight?.chart?.series?.[0]?.type == "HeatMap") {
        return (React.createElement(Heatmap, { isSmall: isSmall, isListChart: isListChart, isReport: isReport, style: style, chart: insight, project: project, runId: runId, updateDrilldownValue: updateDrilldownValue, isHeatmapGlobalScale: isHeatmapGlobalScale, updateIsHeatmapGlobalScale: (isHeatmapGlobalScale) => {
                dispatch(nextReducer.updateIsHeatmapGlobalScale(id, isHeatmapGlobalScale));
            } }));
    }
    // Forgive me father for I have sinned
    if (isComparisonDashboard) {
        const isActualCounts = comparisonDashboardSortValue === "WIN_TOTAL_ACTUAL" || comparisonDashboardSortValue === "POP_TOTAL_ACTUAL";
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.nextChartWrapper },
                hideTitle !== true && (isSmall !== true || showTitle === true) && (React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top", style: { pointerEvents: "none" } }, isDashboard ? (React.createElement("div", null,
                        React.createElement("div", { className: styles.popoverChartTitle }, insight?.narrative?.title),
                        React.createElement("div", null, insight?.narrative?.topText))) : (React.createElement("div", { className: styles.popoverChartTitle }, insight?.narrative?.title))) },
                    React.createElement("div", { className: styles.bTitle +
                            " " +
                            styles.reportChartTitle +
                            " " +
                            (isChatGPT ? styles.isChatChart : "") +
                            " " +
                            (isGoogleMode ? styles.reportChartTitleGoogle : ""), style: isGoogleMode
                            ? {
                                display: "block"
                            }
                            : isDashboard
                                ? {
                                    width: "100%",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontSize: "13px",
                                    fontWeight: 300,
                                    textAlign: "center",
                                    display: "block",
                                    marginTop: "0px",
                                    marginBottom: "5px",
                                    padding: "5px"
                                }
                                : {
                                    textAlign: "left",
                                    marginTop: "0px",
                                    cursor: node?.chart?.isAdded === true ? "pointer" : "default",
                                    whiteSpace: isTableauMode ? "nowrap" : "normal",
                                    background: "transparent",
                                    margin: isExploreTask ? "10px" : isChatGPT ? undefined : "0px",
                                    width: "fit-content"
                                } }, (comparisonOptions
                        ? insight?.narrative?.conditionsLabel || node?.narrative?.conditionsLabel
                        : false) || insight?.narrative?.title))),
                isHexagon !== true &&
                    isSmall !== true &&
                    project?.segments &&
                    project?.segments.length > 1 &&
                    insight?.metadata?.type === "ModelDriver" ? (React.createElement("div", { className: styles.subHold },
                    React.createElement("div", { className: styles.subgroupSelect },
                        React.createElement("div", { className: styles.smallSub }, "Showing Subgroup:"),
                        React.createElement(Select, { options: project.segments.map((segment) => ({
                                label: segment?.conditions?.[0]?.value || "Overall",
                                value: segment?.job?.job_id
                            })), value: currentSegment !== undefined
                                ? {
                                    label: currentSegment?.conditions?.[0]?.value || "Overall",
                                    value: currentSegment?.job?.job_id
                                }
                                : undefined, onChange: (val) => {
                                if (isOptionType(val)) {
                                    updateDrilldownValue(project?.id, val?.value, val?.label);
                                }
                            } })))) : (React.createElement("div", null)),
                React.createElement("div", { className: styles.chartBox +
                        " " +
                        styles.chartBoxComparisonDashboard +
                        " comparisonDashboardBox" +
                        " " +
                        (isChatGPT ? styles.chartBoxChatGPT : ""), style: isExpand && isDashboard
                        ? { height: "calc(100vh - 400px)" }
                        : isDashboard
                            ? { height: "calc(100% - 40px)" }
                            : editInsightLanguageModeEnabled
                                ? { height: hasNarrativeBullets ? "300px" : "355px" }
                                : isSmall
                                    ? { height: "100%", marginTop: "0px" }
                                    : isReportExpand
                                        ? { height: "calc(100vh - 440px)" }
                                        : hasNarrativeBullets
                                            ? { height: "calc(100vh - 600px)" }
                                            : isGoogleMode
                                                ? { height: "300px" }
                                                : {} },
                    React.createElement("div", { className: styles.narrativeHolder, style: { maxHeight: "115px", marginBottom: "10px" } }, (!isGroupedNarrative(comparisonNarrative) &&
                        (comparisonNarrative?.metrics === undefined || isNaN(comparisonNarrative?.difference ?? NaN))) ||
                        (isGroupedNarrative(comparisonNarrative) &&
                            !comparisonNarrative?.metrics?.find((m) => !isNaN(m?.metrics?.difference))) ? (React.createElement("div", { className: styles.comparisonText },
                        React.createElement("span", null, `Not enough data`))) : isGroupedNarrative(comparisonNarrative) ? (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: styles.comparisonLegend, style: { display: "flex", alignItems: "center", flexWrap: "wrap", flexGrow: 1 } }, comparisonNarrative?.metrics?.map((m, i) => {
                            const difference = m?.metrics?.difference;
                            const label = m?.series?.label;
                            const dotColor = modifyRGBValues(color, m?.series, comparisonNarrative, hideChartValues);
                            const isHideLine = isComparisonShowAll ? false : checkIsHidden(label, m?.rank, hideChartValues);
                            return (React.createElement("div", { className: styles.comparisonItemText, key: "firstOrder_" + i, onMouseEnter: () => {
                                    if (label !== undefined && hideChartValues[label]) {
                                        if (showChartValue !== undefined) {
                                            updateShowChartValue(undefined);
                                        }
                                    }
                                    else {
                                        updateShowChartValue(label);
                                    }
                                }, onMouseLeave: () => {
                                    if (showChartValue !== undefined) {
                                        updateShowChartValue(undefined);
                                    }
                                } },
                                React.createElement("div", { className: styles.comparisonChangeText, style: {
                                        color: "#008273"
                                    } },
                                    React.createElement("input", { type: "checkbox", style: {
                                            marginRight: "8px",
                                            marginBottom: "2px"
                                        }, checked: !isHideLine, onChange: () => {
                                            if (label !== undefined) {
                                                updateHideChartValues({
                                                    ...hideChartValues,
                                                    [label]: !isHideLine
                                                });
                                            }
                                            updateShowChartValue(undefined);
                                        } }),
                                    React.createElement("div", { className: styles.comparisonChangeValueText }, (difference > 0 ? "+" : "") + difference.toFixed(2) + "%"),
                                    React.createElement("div", { className: styles.comparisonChangePpText }, "PP")),
                                React.createElement("div", { className: styles.comparisonChangeLabel },
                                    React.createElement("div", { className: styles.comparisonChangeLabelDot, style: {
                                            background: dotColor
                                        } }),
                                    React.createElement("div", null, `${label}`))));
                        })))) : (React.createElement(React.Fragment, null,
                        React.createElement("div", { style: { display: "flex", alignItems: "flex-start", justifyContent: "center" } },
                            React.createElement("div", null,
                                hideChangeInBehavior !== true && (React.createElement("div", { className: styles.comparisonItemText },
                                    React.createElement("div", { className: styles.comparisonChangeText, style: {
                                            color: comparisonNarrative?.difference < 0 ? "#C03E5A" : "#008273"
                                        } },
                                        React.createElement("div", { className: styles.comparisonChangeValueText }, (comparisonNarrative.difference > 0 ? "+" : "") +
                                            comparisonNarrative.difference.toFixed(2) +
                                            "%"),
                                        React.createElement("div", { className: styles.comparisonChangePpText }, "PP")),
                                    React.createElement("div", { className: styles.comparisonChangeLabel },
                                        React.createElement("div", { className: styles.comparisonChangeLabelDot, style: {
                                                background: insight.color
                                            } }),
                                        React.createElement("div", null, `Change in ` + renameBehavior + ` %`)))),
                                hideChangeInOutcome !== true && (React.createElement("div", { className: styles.comparisonItemText },
                                    React.createElement("div", { className: styles.comparisonChangeText, style: {
                                            color: comparisonNarrative?.winsChange < 0 ? "#C03E5A" : "#008273"
                                        } },
                                        React.createElement("div", { className: styles.comparisonChangeValueText }, ((retWinChange) => {
                                            return ((retWinChange > 0 ? "+" : "") +
                                                (isActualCounts
                                                    ? numeral(retWinChange).format("0,0")
                                                    : (retWinChange ?? 0).toFixed(2) + "%"));
                                        })(isActualCounts ? comparisonNarrative?.winsChangeActual : comparisonNarrative?.winsChange))),
                                    React.createElement("div", { className: styles.comparisonChangeLabel },
                                        React.createElement("div", null, `${numeral(comparisonNarrative?.previousWins).format("0,0")} to ${numeral(comparisonNarrative?.newWins).format("0,0")}`)),
                                    React.createElement("div", { className: styles.comparisonChangeLabel },
                                        hideChangeInBehavior === true && (React.createElement("div", { className: styles.comparisonChangeLabelDot, style: {
                                                background: insight.color
                                            } })),
                                        React.createElement("div", null, `Change in ` + renameOutcome))))),
                            insight?.metadata?.properties?.length > 1 ? (React.createElement(React.Fragment, null,
                                React.createElement("div", null,
                                    hideChangeInPopulation !== true && (React.createElement("div", { className: styles.comparisonItemText },
                                        React.createElement("div", { className: styles.comparisonChangeText, style: {
                                                color: comparisonNarrative?.countChange < 0 ? "#C03E5A" : "#008273"
                                            } },
                                            React.createElement("div", { className: styles.comparisonChangeValueText }, (comparisonNarrative.countChange > 0 ? "+" : "") +
                                                comparisonNarrative.countChange.toFixed(2) +
                                                "%"),
                                            React.createElement("div", { className: styles.comparisonChangePpText }, "PP")),
                                        React.createElement("div", { className: styles.comparisonChangeLabel },
                                            React.createElement("div", { className: styles.comparisonChangeLabelDot, style: {
                                                    background: "#B5B4D0"
                                                } }),
                                            React.createElement("div", null, `Change in ` + renamePopulation + ` %`)))),
                                    hideChangeInTotalPopulation !== true && (React.createElement("div", { className: styles.comparisonItemText },
                                        React.createElement("div", { className: styles.comparisonChangeText, style: {
                                                color: comparisonNarrative?.popChange < 0 ? "#C03E5A" : "#008273"
                                            } },
                                            React.createElement("div", { className: styles.comparisonChangeValueText }, ((retPopChange) => (retPopChange > 0 ? "+" : "") +
                                                (isActualCounts
                                                    ? numeral(retPopChange).format("0,0")
                                                    : (retPopChange ?? 0).toFixed(2) + "%"))(isActualCounts
                                                ? comparisonNarrative?.popChangeActual
                                                : comparisonNarrative?.popChange))),
                                        React.createElement("div", { className: styles.comparisonChangeLabel },
                                            React.createElement("div", null, `${numeral(comparisonNarrative?.previousPursued).format("0,0")} to ${numeral(comparisonNarrative?.newPursued).format("0,0")}`)),
                                        React.createElement("div", { className: styles.comparisonChangeLabel },
                                            hideChangeInPopulation === true && (React.createElement("div", { className: styles.comparisonChangeLabelDot, style: {
                                                    background: "#B5B4D0"
                                                } })),
                                            React.createElement("div", null, `Change in ` + renameTotalPopulation + ` (actual)`))))))) : (React.createElement(React.Fragment, null)))))),
                    chartType === ChartSeriesType.Line ? (React.createElement(ResponsiveContainer, { className: isOpenShell
                            ? styles.comparisonDashboardChartContainerOpenShell
                            : styles.comparisonDashboardChartContainer },
                        React.createElement(ComposedChart, { layout: chart.layout, data: chartData, margin: isSmall
                                ? { top: 0, bottom: 0, right: 0, left: 0 }
                                : { top: 10, right: 40, bottom: isExpand ? 40 : 0, left: 10 } },
                            isSmall !== true && React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
                            React.createElement(XAxis, { type: chart.xAxis.type, dataKey: chart.xAxis.dataKey, unit: chart.xAxis.unit, label: {
                                    value: chart.xAxis.label,
                                    position: "insideBottom",
                                    dy: 5
                                }, hide: isSmall && showAxis !== true ? true : false, domain: xAxisMin === undefined ? undefined : [xAxisMin, chartData.length > 1 ? "dataMax" : xAxisMin], tickFormatter: (label) => label?.length > CHART_TICK_LENGTH + 2 ? label.substring(0, CHART_TICK_LENGTH) + "..." : label }),
                            (hideChangeInBehavior !== true || hideChangeInOutcome !== true) && (React.createElement(YAxis, { type: chart.yAxis.type, dataKey: hideChangeInBehavior === true ? "yCount" : chart.yAxis.dataKey, unit: hideChangeInBehavior ? "" : chart.yAxis.unit, label: comparisonOptions && insight?.metadata?.properties?.length > 1 && !isSmall ? (React.createElement(CustomComparisonYAxisLabel, { labelText: hideChangeInBehavior === true ? "Total " + dataset?.outcome : chart.yAxis.label, legendColor: insight.color, rotation: -90 })) : ({
                                    value: "% Change",
                                    position: "center",
                                    angle: -90,
                                    dx: -25
                                }), hide: isSmall && showAxis !== true ? true : false, tick: chart.yAxis.type !== "category", tickFormatter: (tick) => numeral(tick).format("0.[00]a"), domain: hideChangeInBehavior === true ? fixDomain1 : (yDomainOverride ?? yDomain), yAxisId: comparisonOptions && insight?.metadata?.properties?.length > 1 && !isSmall ? 0 : undefined })),
                            (hideChangeInPopulation !== true || hideChangeInTotalPopulation !== true) &&
                                comparisonOptions &&
                                insight?.metadata?.properties?.length > 1 &&
                                !isSmall && (React.createElement(YAxis, { type: chart.yAxis.type, dataKey: hideChangeInPopulation === true ? "yCount" : "otherY", unit: hideChangeInPopulation ? "" : chart.yAxis.unit, label: React.createElement(CustomComparisonYAxisLabel, { labelText: hideChangeInPopulation === true ? "Total Count" : "% of Total Count", rotation: -90, position: "right" }), hide: isSmall && showAxis !== true ? true : false, tick: chart.yAxis.type !== "category", tickFormatter: (tick) => numeral(tick).format("0.[00]a"), domain: hideChangeInPopulation === true ? fixDomain2 : (otherYDomain ?? yDomain), yAxisId: 1, orientation: "right" })),
                            chart.tooltip !== false && showTooltip && (React.createElement(Tooltip, { separator: ": ", cursor: { strokeDasharray: "3 3" }, itemStyle: { color: "#000" }, labelFormatter: () => chart.yAxis.label, offset: 20, formatter: (value, name, props) => {
                                    const series = chart.series.find((s) => value == props.payload[s.dataKey]);
                                    const second = (series?.label ?? series?.labelKey === undefined)
                                        ? undefined
                                        : props.payload[series?.labelKey];
                                    return [isNaN(Number(value)) ? 0 : `${value}`, second ? second : props.payload["label"]];
                                }, position: tooltipPosition, allowEscapeViewBox: { x: false, y: false }, content: React.createElement(CustomTooltip, { id: id, isMaxWidth: node?.isTemporary === true, comparisonOptions: comparisonOptions, outcome: outcome, hideValues: hideChartValues, showAll: isComparisonShowAll, comparisonNarrative: comparisonNarrative, isContinuous: isContinuous, hideChangeInBehavior: hideChangeInBehavior, hideChangeInPopulation: hideChangeInPopulation, hideChangeInOutcome: hideChangeInOutcome, hideChangeInTotalPopulation: hideChangeInTotalPopulation }), ...chart.tooltip })),
                            comparisonOptions?.dateField ? (React.createElement(ReferenceArea, { x1: comparisonReferenceStart, x2: comparisonReferenceEnd, fill: "#7E69D733" })) : (React.createElement(React.Fragment, null)),
                            retSeries.map((s, i) => {
                                const rLabel = s.dataKey === "y" && hideChangeInBehavior === true
                                    ? "Total " + renameOutcome
                                    : s.dataKey === "otherY" && hideChangeInPopulation === true
                                        ? renameTotalPopulation
                                        : s.label;
                                const sProps = {
                                    key: i,
                                    id: String(i),
                                    tooltipType: selected[i] || i === hovered || chart.series.length < 10
                                        ? undefined
                                        : "none",
                                    dataKey: s.dataKey === "y" && hideChangeInBehavior === true
                                        ? "summary.sum"
                                        : s.dataKey === "otherY" && hideChangeInPopulation === true
                                            ? "yCount"
                                            : s.dataKey,
                                    label: rLabel,
                                    unit: s.unit,
                                    isAnimationActive: false,
                                    fill: s?.colorFormatter?.(color, selected[i] || hovered === i) ?? color,
                                    name: rLabel ?? chart.yAxis.label,
                                    ...(s.props ?? {}),
                                    onClick: getHandleSeriesClick(i),
                                    onMouseOver: getHandleSeriesHover(i),
                                    onMouseLeave: () => updateHovered(undefined)
                                };
                                //console.log("what is chartData", chartData);
                                switch (nodeChartType ? nodeChartType : s.type) {
                                    case ChartSeriesType.Line:
                                        if (comparisonOptions?.dateField) {
                                            let fill = sProps.fill;
                                            let yAxisId = comparisonOptions && insight?.metadata?.properties?.length > 1 && !isSmall ? 0 : undefined;
                                            if (sProps.dataKey === "otherY" ||
                                                sProps?.dataKey === "otherYCount" ||
                                                (sProps?.dataKey === "yCount" && hideChangeInPopulation === true)) {
                                                fill = "#B5B4D0";
                                                if (insight?.metadata?.properties?.length > 1 && !isSmall) {
                                                    yAxisId = 1;
                                                }
                                            }
                                            if (isGroupedNarrative(comparisonNarrative)) {
                                                const sMetric = comparisonNarrative?.metrics?.find((m) => m.series?.label === s.label);
                                                let isHidden = false;
                                                if (!isSmall && !isComparisonShowAll) {
                                                    isHidden = checkIsHidden(s.label, sMetric?.rank, hideChartValues, showChartValue);
                                                }
                                                if (isHidden) {
                                                    return React.createElement(React.Fragment, null);
                                                }
                                                let nFill = modifyRGBValues(fill, s, comparisonNarrative, hideChartValues, showChartValue);
                                                if (showChartValue !== undefined && showChartValue !== s?.label) {
                                                    // nFill = "rgba(181, 180, 208, .5)"
                                                    nFill = "#DCDAEC44";
                                                }
                                                // const fillColor = isSmall ? fill : nFill;
                                                const fillColor = nFill;
                                                return (React.createElement(React.Fragment, null,
                                                    React.createElement(Line, { stroke: fillColor, strokeWidth: isSmall ? 3 : 4, type: "linear", connectNulls: true, ...sProps, dot: false, activeDot: isSmall ? false : true }),
                                                    !isSmall && (React.createElement(React.Fragment, null,
                                                        React.createElement(ReferenceLine, { stroke: fillColor, strokeWidth: 3, strokeDasharray: "5 3", segment: [
                                                                {
                                                                    x: comparisonCurrentStart,
                                                                    y: sMetric?.metrics?.newAdjustedWinRate
                                                                },
                                                                {
                                                                    x: comparisonCurrentEnd,
                                                                    y: sMetric?.metrics?.newAdjustedWinRate
                                                                }
                                                            ] }),
                                                        React.createElement(ReferenceLine, { stroke: fillColor, strokeWidth: 3, strokeDasharray: "5 3", segment: [
                                                                {
                                                                    x: comparisonReferenceStart,
                                                                    y: sMetric?.metrics?.previousAdjustedWinRate
                                                                },
                                                                {
                                                                    x: comparisonReferenceEnd,
                                                                    y: sMetric?.metrics?.previousAdjustedWinRate
                                                                }
                                                            ] })))));
                                            }
                                            const referenceKeys = {
                                                previous: "previousAdjustedWinRate",
                                                new: "newAdjustedWinRate"
                                            };
                                            if (sProps.dataKey === "otherY") {
                                                referenceKeys.previous = "previousCountPercentage";
                                                referenceKeys.new = "newCountPercentage";
                                            }
                                            return (React.createElement(React.Fragment, null,
                                                (hideChangeInPopulation !== true ||
                                                    hideChangeInTotalPopulation !== true ||
                                                    yAxisId !== 1) &&
                                                    (hideChangeInBehavior !== true || hideChangeInOutcome !== true || yAxisId !== 0) && (React.createElement(Line, { stroke: fill, strokeWidth: isSmall ? 3 : 4, 
                                                    // dot={isSmall ? false : {stroke: fill, fill: "#FFFFFF", strokeWidth: 3, r: 4}}
                                                    dot: false, activeDot: {
                                                        onClick: getHandleSeriesClick(i),
                                                        onMouseOver: getHandleSeriesHover(i)
                                                    }, type: "linear", connectNulls: true, ...sProps, yAxisId: yAxisId })),
                                                !isSmall && comparisonNarrative !== undefined && (React.createElement(React.Fragment, null,
                                                    comparisonCurrentStart === comparisonCurrentEnd ? (React.createElement(ReferenceLine, { stroke: fill, strokeWidth: 3, strokeDasharray: sProps.dataKey === "otherY" ? "3 5" : "5 3", x: comparisonCurrentStart })) : ((hideChangeInPopulation !== true ||
                                                        hideChangeInTotalPopulation !== true ||
                                                        yAxisId !== 1) &&
                                                        (hideChangeInBehavior !== true ||
                                                            hideChangeInOutcome !== true ||
                                                            yAxisId !== 0) && (React.createElement(ReferenceLine, { stroke: fill, strokeWidth: 3, strokeDasharray: sProps.dataKey === "otherY" ? "3 5" : "5 3", segment: [
                                                            {
                                                                x: comparisonCurrentStart,
                                                                y: hideChangeInBehavior && yAxisId === 0
                                                                    ? comparisonNarrative?.metrics?.newWins /
                                                                        (comparisonNarrative?.metrics?.currentDateBuckets ?? 1)
                                                                    : hideChangeInPopulation && yAxisId === 1
                                                                        ? comparisonNarrative?.metrics?.newPursued /
                                                                            (comparisonNarrative?.metrics?.currentDateBuckets ?? 1)
                                                                        : comparisonNarrative.metrics?.[referenceKeys.new]
                                                            },
                                                            {
                                                                x: comparisonCurrentEnd,
                                                                y: hideChangeInBehavior && yAxisId === 0
                                                                    ? comparisonNarrative?.metrics?.newWins /
                                                                        (comparisonNarrative?.metrics?.currentDateBuckets ?? 1)
                                                                    : hideChangeInPopulation && yAxisId === 1
                                                                        ? comparisonNarrative?.metrics?.newPursued /
                                                                            (comparisonNarrative?.metrics?.currentDateBuckets ?? 1)
                                                                        : comparisonNarrative.metrics?.[referenceKeys.new]
                                                            }
                                                        ], yAxisId: yAxisId }))),
                                                    comparisonReferenceStart === comparisonReferenceEnd ? (React.createElement(ReferenceLine, { stroke: fill, strokeWidth: 3, strokeDasharray: sProps.dataKey === "otherY" ? "3 5" : "5 3", x: comparisonReferenceStart })) : ((hideChangeInPopulation !== true ||
                                                        hideChangeInTotalPopulation !== true ||
                                                        yAxisId !== 1) &&
                                                        (hideChangeInBehavior !== true ||
                                                            hideChangeInOutcome !== true ||
                                                            yAxisId !== 0) && (React.createElement(ReferenceLine, { stroke: fill, strokeWidth: 3, strokeDasharray: sProps.dataKey === "otherY" ? "3 5" : "5 3", segment: [
                                                            {
                                                                x: comparisonReferenceStart,
                                                                y: hideChangeInBehavior && yAxisId === 0
                                                                    ? comparisonNarrative?.metrics?.previousWins /
                                                                        (comparisonNarrative?.metrics?.referenceDateBuckets ?? 1)
                                                                    : hideChangeInPopulation && yAxisId === 1
                                                                        ? comparisonNarrative?.metrics?.previousPursued /
                                                                            (comparisonNarrative?.metrics?.referenceDateBuckets ?? 1)
                                                                        : comparisonNarrative.metrics?.[referenceKeys.previous]
                                                            },
                                                            {
                                                                x: comparisonReferenceEnd,
                                                                y: hideChangeInBehavior && yAxisId === 0
                                                                    ? comparisonNarrative?.metrics?.previousWins /
                                                                        (comparisonNarrative?.metrics?.referenceDateBuckets ?? 1)
                                                                    : hideChangeInPopulation && yAxisId === 1
                                                                        ? comparisonNarrative?.metrics?.previousPursued /
                                                                            (comparisonNarrative?.metrics?.referenceDateBuckets ?? 1)
                                                                        : comparisonNarrative.metrics?.[referenceKeys.previous]
                                                            }
                                                        ], yAxisId: yAxisId })))))));
                                        }
                                        return (React.createElement(React.Fragment, null,
                                            React.createElement(Line, { stroke: sProps.fill, strokeWidth: isSmall ? 3 : 3, dot: false, activeDot: {
                                                    onClick: getHandleSeriesClick(i),
                                                    onMouseOver: getHandleSeriesHover(i)
                                                }, type: "stepBefore", connectNulls: true, ...sProps })));
                                    case ChartSeriesType.Bar:
                                    default:
                                        return React.createElement(Bar, { ...sProps });
                                }
                            })))) : (React.createElement(ResponsiveContainer, { className: styles.comparisonDashboardChartContainer },
                        React.createElement(BarChart, { layout: chart.layout, data: isHexagon && insight?.metadata?.type !== "ModelDriver"
                                ? chartData.slice(0, Math.ceil(20 / (chart?.series?.length || 1)))
                                : chartData, margin: isDashboard
                                ? { top: 0, bottom: 0, left: 0, right: 10 }
                                : isListChart
                                    ? { top: 0, bottom: 0, left: 10, right: 10 }
                                    : isSmall
                                        ? { top: 10, bottom: 10, right: 10, left: 10 }
                                        : { top: 10, right: 40, bottom: 0, left: 10 }, onMouseMove: (e) => {
                                if (e?.activeTooltipIndex !== undefined && isHighlight !== e?.activeTooltipIndex) {
                                    updateIsHighlight(e.activeTooltipIndex);
                                }
                            }, onMouseLeave: () => updateIsHighlight(undefined) },
                            isSmall !== true && React.createElement(CartesianGrid, { strokeDasharray: "3 3", strokeOpacity: ".6" }),
                            React.createElement(XAxis, { type: chart.xAxis.type, dataKey: chart.xAxis.dataKey, unit: chart.xAxis.unit, label: isExpand
                                    ? {
                                        value: chart.xAxis.label,
                                        position: "insideBottom",
                                        dy: -20
                                    }
                                    : isDashboard
                                        ? undefined
                                        : {
                                            value: chart.xAxis.label,
                                            position: "insideBottom",
                                            dy: 5
                                        }, hide: isDashboard ? true : isSmall && showAxis !== true ? true : false, domain: xAxisMin === undefined ? undefined : [xAxisMin, chartData.length > 1 ? "dataMax" : xAxisMin], interval: showAll ? 0 : 0, minTickGap: showAll ? 0 : 1, height: isDashboard && isExpand
                                    ? (40 + longestName) * 4.5
                                    : isDashboard
                                        ? undefined
                                        : (14 + Math.min(longestName, CHART_TICK_LENGTH)) * 4.5, tick: isDashboard ? undefined : (React.createElement(CustomizedAxisTick, { showAll: showAll, isHighlight: isHighlight, summaryValue: hoverLinkIndex })) }),
                            React.createElement(YAxis, { type: chart.yAxis.type, dataKey: chart.yAxis.dataKey, unit: isDashboard ? undefined : chart.yAxis.unit, label: isDashboard
                                    ? undefined
                                    : {
                                        value: chart.yAxis.label,
                                        position: "center",
                                        angle: -90,
                                        dx: -25
                                    }, hide: isSmall && showAxis !== true ? true : false, tick: chart.yAxis.type !== "category", tickFormatter: (tick) => numeral(tick).format("0.[00]a"), domain: yDomain }),
                            chart.tooltip !== false && showTooltip && (React.createElement(Tooltip, { separator: ": ", cursor: { fill: "#EFECF6", opacity: ".8" }, itemStyle: { color: "#000" }, labelFormatter: () => chart.yAxis.label, offset: 20, formatter: (value, name, props) => {
                                    const series = chart.series.find((s) => value == props.payload[s.dataKey]);
                                    const second = (series?.label ?? series?.labelKey === undefined)
                                        ? undefined
                                        : props.payload[series?.labelKey];
                                    return [isNaN(Number(value)) ? 0 : `${value}`, second ? second : props.payload["label"]];
                                }, allowEscapeViewBox: isInfographic ? { x: true, y: true } : { x: false, y: false }, content: React.createElement(CustomTooltip, { id: id, isMaxWidth: node?.isTemporary === true, isContinuous: isContinuous }), position: tooltipPosition
                                    ? tooltipPosition
                                    : isInfographic
                                        ? { x: 0, y: -300 }
                                        : isDashboard
                                            ? { x: -400, y: -40 }
                                            : undefined, wrapperStyle: isInfographic
                                    ? { minWidth: 700 }
                                    : isDashboard
                                        ? {
                                            minWidth: 400
                                        }
                                        : undefined, ...chart.tooltip })),
                            chart.tooltip !== false && showTooltip && hideLegend !== true && (React.createElement(Legend, { formatter: (value, entry) => {
                                    if (insight?.metadata?.properties.length === 1) {
                                        return insight?.chart?.yAxis?.label;
                                    }
                                    if (entry?.dataKey === "otherY" || entry?.dataKey === "otherYTotal") {
                                        return insight?.metadata?.properties?.[1]?.column + " is all other";
                                    }
                                    else {
                                        return insight?.narrative?.conditionsLabel;
                                    }
                                } })),
                            retSeries.map((s, i) => {
                                const name = s.label ?? chart.yAxis.label;
                                const sProps = {
                                    key: i,
                                    id: String(i),
                                    tooltipType: selected[i] || i === hovered || retSeries.length < 10 ? undefined : "none",
                                    dataKey: s.dataKey,
                                    label: s.label,
                                    unit: s.unit,
                                    isAnimationActive: false,
                                    fill: i === 1 ? "#999" : (s?.colorFormatter?.(color, selected[i] || hovered === i) ?? color),
                                    name: name,
                                    onClick: getHandleSeriesClick(i)
                                };
                                /*
                                                      isListChart &&
                                    i === this.props?.chart?.narrative?.summaryValueIndex
                                      ? "#F47C20"
                                      */
                                return insight?.metadata?.type === "ModelDriver" ? (React.createElement(Bar, { ...sProps }, chartData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: (entry.y ?? 0) > 0 ? "rgba(125, 105, 215, 1)" : "rgba(213, 107, 39, 1)" }))))) : (React.createElement(Bar, { ...sProps }, chartData.map((entry, index) => highlightProps === entry?.name ||
                                    ((isListChart || hoverLinkIndex !== undefined) &&
                                        insight.metadata.properties &&
                                        insight.metadata.properties.length === 2 &&
                                        entry?.name === hoverLinkIndex) ? (React.createElement(Cell, { key: `cell-${index}`, stroke: "#34125F", strokeWidth: isListChart ? 2 : 3 })) : (React.createElement(Cell, { key: `cell-${index}`, opacity: hoverLinkIndex === undefined ? 1 : hoverLinkIndex === entry?.name ? 1 : 0.5 })))));
                            }))))),
                narrativeText !== undefined ? (React.createElement("div", { className: styles.narrativeHolder, style: { pointerEvents: "none" } }, narrativeText)) : (React.createElement("div", null))),
            isSmall !== true && isHideNarrative !== true && (React.createElement("div", { className: (isExploreTask ? styles.cBotExplore : styles.cBot) +
                    " " +
                    (isGoogleMode ? styles.cBotGoogle : "") +
                    " " +
                    (isOpenShell ? styles.cBotComparisonDashboardOpenShell : styles.cBotComparisonDashboard) },
                showLeakage && !comparisonOptions && (React.createElement("div", { className: styles.cBotMuscle, style: isOpenShell ? { whiteSpace: "pre-wrap" } : {} },
                    React.createElement(LeakageChartContainer, { id: id, height: isExploreTask ? 400 : 200, width: isExploreTask ? 400 : 200, isExploreTask: isExploreTask, node: node, isSmall: true, isReport: true, insight: insight, datasetId: dataset?.id }))),
                !comparisonOptions && (React.createElement(ChartNarrative, { id: id, insight: insight, node: node, highlightProps: highlightProps, editInsightLanguageModeEnabled: editInsightLanguageModeEnabled, updateEditInsightLanguageModeEnabled: updateEditInsightLanguageModeEnabled, updateHoverLinkIndex: updateHoverLinkIndex, stateNodeChartType: stateNodeChartType, updateStateNodeChartType: updateStateNodeChartType, isExploreTask: isExploreTask, isChatGPT: isChatGPT, isSmall: isSmall, isShowMore: isShowMore }))))));
    }
    return (React.createElement(React.Fragment, null,
        hideTitle !== true && (isSmall !== true || showTitle === true) && (React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top", style: { pointerEvents: "none" } }, isDashboard ? (React.createElement("div", null,
                React.createElement("div", { className: styles.popoverChartTitle }, insight?.narrative?.title),
                React.createElement("div", null, insight?.narrative?.topText))) : (React.createElement("div", { className: styles.popoverChartTitle }, insight?.narrative?.title))) },
            React.createElement("div", { className: styles.bTitle + " " + styles.reportChartTitle + " " + (isGoogleMode ? styles.reportChartTitleGoogle : ""), style: isGoogleMode
                    ? {
                        display: "block"
                    }
                    : isDashboard
                        ? {
                            width: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "13px",
                            fontWeight: 300,
                            textAlign: "center",
                            display: "block",
                            marginTop: "0px",
                            marginBottom: "5px",
                            padding: "5px"
                        }
                        : {
                            textAlign: "left",
                            marginTop: "0px",
                            cursor: node?.chart?.isAdded === true ? "pointer" : "default",
                            whiteSpace: isTableauMode ? "nowrap" : "normal",
                            background: "transparent",
                            margin: isExploreTask ? "10px" : "0px",
                            width: "fit-content"
                        } }, (comparisonOptions ? insight?.narrative?.conditionsLabel || node?.narrative?.conditionsLabel : false) ||
                insight?.narrative?.title))),
        isHexagon !== true &&
            isSmall !== true &&
            project?.segments &&
            project?.segments.length > 1 &&
            insight.metadata?.type === "ModelDriver" ? (React.createElement("div", { className: styles.subHold },
            React.createElement("div", { className: styles.subgroupSelect },
                React.createElement("div", { className: styles.smallSub }, "Showing Subgroup:"),
                React.createElement(Select, { options: project.segments.map((segment) => ({
                        label: segment?.conditions?.[0]?.value || "Overall",
                        value: segment?.job?.job_id
                    })), value: currentSegment !== undefined
                        ? {
                            label: currentSegment?.conditions?.[0]?.value || "Overall",
                            value: currentSegment?.job?.job_id
                        }
                        : undefined, onChange: (val) => {
                        if (isOptionType(val)) {
                            updateDrilldownValue(project?.id, val.value, val.label);
                        }
                    } })))) : (React.createElement("div", null)),
        React.createElement("div", { className: styles.chartBox + " " + (isChatGPT ? styles.chartBoxChatGPT : ""), style: isExpand && isDashboard
                ? { height: "calc(100vh - 400px)" }
                : isDashboard
                    ? { height: "calc(100% - 40px)" }
                    : editInsightLanguageModeEnabled
                        ? { height: hasNarrativeBullets ? "300px" : "355px" }
                        : isSmall
                            ? { height: "100%", marginTop: "0px" }
                            : isReportExpand
                                ? { height: "calc(100vh - 440px)" }
                                : hasNarrativeBullets
                                    ? { height: "calc(100vh - 600px)" }
                                    : isGoogleMode
                                        ? { height: "300px" }
                                        : {} }, chartType === ChartSeriesType.Line ? (React.createElement(ResponsiveContainer, null,
            React.createElement(ComposedChart, { layout: chart.layout, data: chartData, margin: isSmall ? { top: 0, bottom: 0, right: 0, left: 0 } : { top: 10, right: 40, bottom: 0, left: 10 } },
                isSmall !== true && React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
                React.createElement(XAxis, { type: chart.xAxis.type, dataKey: chart.xAxis.dataKey, unit: chart.xAxis.unit, label: {
                        value: chart.xAxis.label,
                        position: "insideBottom",
                        dy: 5
                    }, hide: isSmall && showAxis !== true ? true : false, domain: xAxisMin === undefined ? undefined : [xAxisMin, chartData.length > 1 ? "dataMax" : xAxisMin], tickFormatter: (label) => label?.length > CHART_TICK_LENGTH + 2 ? label.substring(0, CHART_TICK_LENGTH) + "..." : label }),
                React.createElement(YAxis, { type: chart.yAxis.type, dataKey: chart.yAxis.dataKey, unit: chart.yAxis.unit, label: comparisonOptions && insight?.metadata?.properties?.length > 1 && !isSmall ? (React.createElement(CustomComparisonYAxisLabel, { labelText: chart.yAxis.label, legendColor: insight.color, rotation: -90, fontSize: 12 })) : ({
                        value: chart.yAxis.label,
                        position: "center",
                        angle: -90,
                        dx: -25
                    }), hide: isSmall && showAxis !== true ? true : false, tick: chart.yAxis.type !== "category", tickFormatter: (tick) => numeral(tick).format("0.[00]a"), domain: yDomain, yAxisId: comparisonOptions && insight?.metadata?.properties?.length > 1 && !isSmall ? 0 : undefined }),
                comparisonOptions && insight?.metadata?.properties?.length > 1 && !isSmall && (React.createElement(YAxis, { type: chart.yAxis.type, dataKey: "otherY", unit: chart.yAxis.unit, label: React.createElement(CustomComparisonYAxisLabel, { labelText: "% of Total Count", rotation: -90, position: "right", fontSize: 12 }), hide: isSmall && showAxis !== true ? true : false, tick: chart.yAxis.type !== "category", tickFormatter: (tick) => numeral(tick).format("0.[00]a"), domain: otherYDomain ?? yDomain, yAxisId: 1, orientation: "right" })),
                chart.tooltip !== false && showTooltip && (React.createElement(Tooltip, { separator: ": ", cursor: { strokeDasharray: "3 3" }, itemStyle: { color: "#000" }, labelFormatter: () => chart.yAxis.label, offset: 20, formatter: (value, name, props) => {
                        const series = chart.series.find((s) => value == props.payload[s.dataKey]);
                        const second = (series?.label ?? series?.labelKey === undefined) ? undefined : props.payload[series?.labelKey];
                        return [isNaN(Number(value)) ? 0 : `${value}`, second ? second : props.payload["label"]];
                    }, position: tooltipPosition ? tooltipPosition : undefined, allowEscapeViewBox: { x: false, y: false }, content: React.createElement(CustomTooltip, { id: id, isMaxWidth: node?.isTemporary === true, comparisonOptions: comparisonOptions, outcome: outcome, hideValues: hideChartValues, comparisonNarrative: comparisonNarrative, isContinuous: isContinuous }), ...chart.tooltip })),
                chart.tooltip !== false &&
                    showTooltip &&
                    !(comparisonOptions && insight.metadata.properties.length == 1) && React.createElement(Legend, null),
                comparisonOptions?.dateField ? (React.createElement(ReferenceArea, { x1: comparisonCurrentStart, x2: comparisonCurrentEnd, fill: "#7E69D733" })) : (React.createElement(React.Fragment, null)),
                retSeries.map((s, i) => {
                    const sProps = {
                        key: i,
                        id: String(i),
                        tooltipType: selected[i] || i === hovered || chart.series.length < 10 ? undefined : "none",
                        dataKey: s.dataKey,
                        label: s.label,
                        unit: s.unit,
                        isAnimationActive: false,
                        fill: s?.colorFormatter?.(color, selected[i] || hovered === i) ?? color,
                        name: s.label ?? chart.yAxis.label,
                        ...(s.props ?? {}),
                        onClick: getHandleSeriesClick(i),
                        onMouseOver: getHandleSeriesHover(i),
                        onMouseLeave: () => updateHovered(undefined)
                    };
                    switch (stateNodeChartType ? stateNodeChartType : s.type) {
                        case ChartSeriesType.Line:
                            if (comparisonOptions?.dateField) {
                                let fill = sProps.fill;
                                let yAxisId = comparisonOptions && insight?.metadata?.properties?.length > 1 && !isSmall ? 0 : undefined;
                                if (sProps.dataKey === "otherY") {
                                    fill = "#B5B4D0";
                                    if (insight?.metadata?.properties?.length > 1 && !isSmall) {
                                        yAxisId = 1;
                                    }
                                }
                                if (isGroupedNarrative(comparisonNarrative)) {
                                    const sMetric = comparisonNarrative?.metrics?.find((m) => m.series?.label === s.label);
                                    let isHidden = false;
                                    if (!isSmall) {
                                        isHidden = checkIsHidden(s.label, sMetric?.rank, hideChartValues, showChartValue);
                                    }
                                    if (isHidden) {
                                        return React.createElement(React.Fragment, null);
                                    }
                                    let nFill = modifyRGBValues(fill, s, comparisonNarrative, hideChartValues, showChartValue);
                                    if (showChartValue !== undefined && showChartValue !== s?.label) {
                                        // nFill = "rgba(181, 180, 208, .5)"
                                        nFill = "#DCDAEC44";
                                    }
                                    // const fillColor = isSmall ? fill : nFill;
                                    const fillColor = nFill;
                                    return (React.createElement(React.Fragment, null,
                                        React.createElement(Line, { stroke: fillColor, strokeWidth: isSmall ? 3 : 4, type: "linear", connectNulls: true, ...sProps, dot: false, activeDot: isSmall ? false : true }),
                                        !isSmall && (React.createElement(React.Fragment, null,
                                            React.createElement(ReferenceLine, { stroke: fillColor, strokeWidth: 3, strokeDasharray: "5 3", segment: [
                                                    {
                                                        x: comparisonCurrentStart,
                                                        y: sMetric?.metrics?.newAdjustedWinRate
                                                    },
                                                    {
                                                        x: comparisonCurrentEnd,
                                                        y: sMetric?.metrics?.newAdjustedWinRate
                                                    }
                                                ] }),
                                            React.createElement(ReferenceLine, { stroke: fillColor, strokeWidth: 3, strokeDasharray: "5 3", segment: [
                                                    {
                                                        x: comparisonReferenceStart,
                                                        y: sMetric?.metrics?.previousAdjustedWinRate
                                                    },
                                                    {
                                                        x: comparisonReferenceEnd,
                                                        y: sMetric?.metrics?.previousAdjustedWinRate
                                                    }
                                                ] })))));
                                }
                                const referenceKeys = {
                                    previous: "previousAdjustedWinRate",
                                    new: "newAdjustedWinRate"
                                };
                                if (sProps.dataKey === "otherY") {
                                    referenceKeys.previous = "previousCountPercentage";
                                    referenceKeys.new = "newCountPercentage";
                                }
                                return (React.createElement(React.Fragment, null,
                                    React.createElement(Line, { stroke: fill, strokeWidth: isSmall ? 3 : 4, 
                                        // dot={isSmall ? false : {stroke: fill, fill: "#FFFFFF", strokeWidth: 3, r: 4}}
                                        dot: false, activeDot: {
                                            onClick: getHandleSeriesClick(i),
                                            onMouseOver: getHandleSeriesHover(i)
                                        }, type: "linear", connectNulls: true, ...sProps, yAxisId: yAxisId }),
                                    !isSmall && comparisonNarrative !== undefined && (React.createElement(React.Fragment, null,
                                        React.createElement(ReferenceLine, { stroke: fill, strokeWidth: 3, strokeDasharray: sProps.dataKey === "otherY" ? "3 5" : "5 3", segment: [
                                                {
                                                    x: comparisonCurrentStart,
                                                    y: comparisonNarrative.metrics?.[referenceKeys.new]
                                                },
                                                {
                                                    x: comparisonCurrentEnd,
                                                    y: comparisonNarrative.metrics?.[referenceKeys.new]
                                                }
                                            ], yAxisId: yAxisId }),
                                        React.createElement(ReferenceLine, { stroke: fill, strokeWidth: 3, strokeDasharray: sProps.dataKey === "otherY" ? "3 5" : "5 3", segment: [
                                                {
                                                    x: comparisonReferenceStart,
                                                    y: comparisonNarrative.metrics?.[referenceKeys.previous]
                                                },
                                                {
                                                    x: comparisonReferenceEnd,
                                                    y: comparisonNarrative.metrics?.[referenceKeys.previous]
                                                }
                                            ], yAxisId: yAxisId })))));
                            }
                            return (React.createElement(React.Fragment, null,
                                React.createElement(Line, { stroke: sProps.fill, strokeWidth: isSmall ? 1 : 3, dot: false, activeDot: {
                                        onClick: getHandleSeriesClick(i),
                                        onMouseOver: getHandleSeriesHover(i)
                                    }, type: "stepBefore", connectNulls: true, ...sProps })));
                        case ChartSeriesType.Bar:
                        default:
                            return React.createElement(Bar, { ...sProps });
                    }
                })))) : (React.createElement(ResponsiveContainer, null,
            React.createElement(BarChart, { layout: chart.layout, data: isHexagon && insight?.metadata?.type !== "ModelDriver"
                    ? chartData.slice(0, Math.ceil(20 / (chart?.series?.length || 1)))
                    : chartData, margin: isDashboard
                    ? { top: 0, bottom: 0, left: 0, right: 10 }
                    : isListChart
                        ? { top: 0, bottom: 0, left: 10, right: 10 }
                        : isSmall
                            ? { top: 10, bottom: 10, right: 10, left: 10 }
                            : { top: 10, right: 40, bottom: 0, left: 10 }, onMouseMove: (e) => {
                    if (e?.activeTooltipIndex !== undefined && isHighlight !== e?.activeTooltipIndex) {
                        updateIsHighlight(e.activeTooltipIndex);
                    }
                }, onMouseLeave: () => updateIsHighlight(undefined) },
                isSmall !== true && React.createElement(CartesianGrid, { strokeDasharray: "3 3", strokeOpacity: ".6" }),
                React.createElement(XAxis, { type: chart.xAxis.type, dataKey: chart.xAxis.dataKey, unit: chart.xAxis.unit, label: isExpand
                        ? {
                            value: chart.xAxis.label,
                            position: "insideBottom",
                            dy: -20
                        }
                        : isDashboard
                            ? undefined
                            : {
                                value: chart.xAxis.label,
                                position: "insideBottom",
                                dy: 5
                            }, hide: isDashboard ? true : isSmall && showAxis !== true ? true : false, domain: xAxisMin === undefined ? undefined : [xAxisMin, chartData.length > 1 ? "dataMax" : xAxisMin], interval: showAll ? 0 : 0, minTickGap: showAll ? 0 : 1, height: isDashboard && isExpand
                        ? (40 + longestName) * 4.5
                        : isDashboard
                            ? undefined
                            : (14 + Math.min(longestName, CHART_TICK_LENGTH)) * 4.5, tick: isDashboard ? undefined : (React.createElement(CustomizedAxisTick, { showAll: showAll, isHighlight: isHighlight, summaryValue: hoverLinkIndex })) }),
                React.createElement(YAxis, { type: chart.yAxis.type, dataKey: chart.yAxis.dataKey, unit: isDashboard ? undefined : chart.yAxis.unit, label: isDashboard
                        ? undefined
                        : {
                            value: chart.yAxis.label,
                            position: "center",
                            angle: -90,
                            dx: -25
                        }, hide: isSmall && showAxis !== true ? true : false, tick: chart.yAxis.type !== "category", tickFormatter: (tick) => numeral(tick).format("0.[00]a"), domain: yDomain }),
                chart.tooltip !== false && showTooltip && (React.createElement(Tooltip, { separator: ": ", cursor: { fill: "#EFECF6", opacity: ".8" }, itemStyle: { color: "#000" }, labelFormatter: () => chart.yAxis.label, offset: 20, formatter: (value, name, props) => {
                        const series = chart.series.find((s) => value == props.payload[s.dataKey]);
                        const second = (series?.label ?? series?.labelKey === undefined) ? undefined : props.payload[series?.labelKey];
                        return [isNaN(Number(value)) ? 0 : `${value}`, second ? second : props.payload["label"]];
                    }, allowEscapeViewBox: isInfographic ? { x: true, y: true } : { x: false, y: false }, content: React.createElement(CustomTooltip, { id: id, isMaxWidth: node?.isTemporary === true, isContinuous: isContinuous }), position: tooltipPosition
                        ? tooltipPosition
                        : isInfographic
                            ? { x: 0, y: -300 }
                            : isDashboard
                                ? { x: -400, y: -40 }
                                : undefined, wrapperStyle: isInfographic
                        ? { minWidth: 700 }
                        : isDashboard
                            ? {
                                minWidth: 400
                            }
                            : undefined, ...chart.tooltip })),
                chart.tooltip !== false && showTooltip && hideLegend !== true && (React.createElement(Legend, { formatter: (value, entry) => {
                        if (insight?.metadata?.properties.length === 1) {
                            return insight?.chart?.yAxis?.label;
                        }
                        if (entry?.dataKey === "otherY" || entry?.dataKey === "otherYTotal") {
                            return insight?.metadata?.properties?.[1]?.column + " is all other";
                        }
                        else {
                            return insight?.narrative?.conditionsLabel;
                        }
                    } })),
                retSeries.map((s, i) => {
                    const name = s.label ?? chart.yAxis.label;
                    const sProps = {
                        key: i,
                        id: String(i),
                        tooltipType: selected[i] || i === hovered || retSeries.length < 10 ? undefined : "none",
                        dataKey: s.dataKey,
                        label: s.label,
                        unit: s.unit,
                        isAnimationActive: false,
                        fill: i === 1 ? "#999" : (s?.colorFormatter?.(color, selected[i] || hovered === i) ?? color),
                        name: name,
                        onClick: getHandleSeriesClick(i)
                    };
                    /*
                                            isListChart &&
                          i === this.props?.chart?.narrative?.summaryValueIndex
                            ? "#F47C20"
                            */
                    return insight?.metadata?.type === "ModelDriver" ? (React.createElement(Bar, { ...sProps }, chartData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: (entry.y ?? 0 > 0) ? "rgba(125, 105, 215, 1)" : "rgba(213, 107, 39, 1)" }))))) : (React.createElement(Bar, { ...sProps }, chartData.map((entry, index) => highlightProps === entry?.name ||
                        ((isListChart || hoverLinkIndex !== false) &&
                            insight.metadata?.properties &&
                            insight.metadata?.properties.length === 2 &&
                            entry?.name === hoverLinkIndex) ? (React.createElement(Cell, { key: `cell-${index}`, stroke: "#34125F", strokeWidth: isListChart ? 2 : 3 })) : (React.createElement(Cell, { key: `cell-${index}`, opacity: hoverLinkIndex === false || hoverLinkIndex === undefined
                            ? 1
                            : hoverLinkIndex === entry?.name
                                ? 1
                                : 0.5 })))));
                }))))),
        narrativeText !== undefined ? (React.createElement("div", { className: styles.narrativeHolder, style: { pointerEvents: "none" } }, narrativeText)) : (React.createElement("div", null)),
        isSmall !== true && isHideNarrative !== true && (React.createElement("div", { className: (isExploreTask ? styles.cBotExplore : styles.cBot) + " " + (isGoogleMode ? styles.cBotGoogle : "") },
            showLeakage && !comparisonOptions && (React.createElement("div", { className: styles.cBotMuscle },
                React.createElement(LeakageChartContainer, { id: id, height: isExploreTask ? 400 : 200, width: isExploreTask ? 400 : 200, isExploreTask: isExploreTask, node: node, isSmall: true, isReport: true, insight: insight, datasetId: dataset?.id }))),
            comparisonOptions ? (React.createElement("div", { className: styles.narrativeHolder }, (!isGroupedNarrative(comparisonNarrative) &&
                (comparisonNarrative?.metrics === undefined || isNaN(comparisonNarrative?.difference))) ||
                (isGroupedNarrative(comparisonNarrative) &&
                    !comparisonNarrative?.metrics?.find((m) => !isNaN(m?.metrics?.difference))) ? (React.createElement("div", { className: styles.comparisonText },
                React.createElement("span", null, `Not enough data`))) : isGroupedNarrative(comparisonNarrative) ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.comparisonText },
                    React.createElement("span", null, `From ${comparisonOptions?.currentStart} to ${comparisonOptions?.currentEnd}, `),
                    React.createElement("strong", null, `${insight?.narrative?.conditionsLabel || insight?.narrative?.title}`),
                    React.createElement("span", null, ` saw the following changes in % of Total ${successLabel}: `)),
                React.createElement("div", { className: styles.comparisonLegend }, comparisonNarrative?.metrics?.map((m, i) => {
                    const difference = m?.metrics?.difference;
                    const label = m?.series?.label;
                    const dotColor = modifyRGBValues(color, m?.series, comparisonNarrative, hideChartValues);
                    const isHideLine = checkIsHidden(label, m?.rank, hideChartValues);
                    return (React.createElement("div", { className: styles.comparisonLegendItem, key: "firstOrder_" + i, onMouseEnter: () => {
                            if (label !== undefined && hideChartValues[label]) {
                                if (showChartValue !== undefined) {
                                    updateShowChartValue(undefined);
                                }
                            }
                            else {
                                updateShowChartValue(label);
                            }
                        }, onMouseLeave: () => {
                            if (showChartValue !== undefined) {
                                updateShowChartValue(undefined);
                            }
                        } },
                        React.createElement("input", { type: "checkbox", style: {
                                marginRight: "8px",
                                marginBottom: "2px"
                            }, checked: !isHideLine, onChange: () => {
                                if (label !== undefined) {
                                    updateHideChartValues({
                                        ...hideChartValues,
                                        [label]: !isHideLine
                                    });
                                }
                                updateShowChartValue(undefined);
                            } }),
                        React.createElement("span", { style: { marginRight: "4px" } },
                            React.createElement("div", { style: {
                                    width: "15px",
                                    height: "15px",
                                    borderRadius: "10px",
                                    background: dotColor,
                                    marginBottom: "-2px",
                                    display: "inline-block"
                                } })),
                        React.createElement("strong", null, `${label}`),
                        React.createElement("span", null, `: `),
                        React.createElement("strong", { style: {
                                color: difference < 0 ? "#C03E5A" : "#54C06C",
                                fontWeight: "bold"
                            } }, (difference > 0 ? "+" : "") + difference.toFixed(2) + "% pp")));
                })))) : (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.comparisonText },
                    React.createElement("span", null, `From ${comparisonOptions?.currentStart} to ${comparisonOptions?.currentEnd}, `),
                    React.createElement("strong", null, `${insight?.narrative?.conditionsLabel || insight?.narrative?.title}`),
                    React.createElement("span", null, ` saw the following changes: `)),
                React.createElement("ul", { style: { paddingLeft: "20px" } },
                    React.createElement("li", { className: styles.comparisonText },
                        React.createElement("span", { style: { marginRight: "4px" } },
                            React.createElement("div", { style: {
                                    width: "15px",
                                    height: "15px",
                                    borderRadius: "10px",
                                    background: insight.color,
                                    marginBottom: "-2px",
                                    display: "inline-block"
                                } })),
                        React.createElement("span", null, `Change in Behavior (% of Total ${successLabel}): `),
                        React.createElement("strong", { style: {
                                color: comparisonNarrative?.difference < 0 ? "#C03E5A" : "#54C06C",
                                fontWeight: "bold"
                            } }, (comparisonNarrative.difference > 0 ? "+" : "") +
                            comparisonNarrative.difference.toFixed(2) +
                            "% pp")),
                    insight?.metadata?.properties?.length > 1 ? (React.createElement(React.Fragment, null,
                        React.createElement("li", { className: styles.comparisonText },
                            React.createElement("span", { style: { marginRight: "4px" } },
                                React.createElement("div", { style: {
                                        width: "15px",
                                        height: "15px",
                                        borderRadius: "10px",
                                        background: "#B5B4D0",
                                        marginBottom: "-2px",
                                        display: "inline-block"
                                    } })),
                            React.createElement("span", null, "Change in Population (% of Total Count): "),
                            React.createElement("strong", { style: {
                                    color: comparisonNarrative?.countChange < 0 ? "#C03E5A" : "#54C06C",
                                    fontWeight: "bold"
                                } }, (comparisonNarrative.countChange > 0 ? "+" : "") +
                                comparisonNarrative.countChange.toFixed(2) +
                                "% pp")))) : (React.createElement(React.Fragment, null))))))) : (React.createElement(ChartNarrative, { id: id, insight: insight, node: node, highlightProps: highlightProps, editInsightLanguageModeEnabled: editInsightLanguageModeEnabled, updateEditInsightLanguageModeEnabled: updateEditInsightLanguageModeEnabled, updateHoverLinkIndex: updateHoverLinkIndex, stateNodeChartType: stateNodeChartType, updateStateNodeChartType: updateStateNodeChartType, isExploreTask: isExploreTask, isChatGPT: isChatGPT, isSmall: isSmall, isShowMore: isShowMore }))))));
};
const NextChartWrapper = (props) => {
    const [isHoverMouse, updateIsHoverMouse] = useState(false);
    const chartId = (props.isReport ? "nextChartReport_" : "nextChart_") + props.insight.narrative.title;
    const ChartComponent = useMemo(() => React.createElement(NextChart, { ...props }), [
        props.highlightProps,
        props.isReportExpand,
        props.insight.metadata.type,
        props.insight.narrative.title,
        props.insight.chart?.yAxis?.label,
        props.reportChartSort,
        props.isExpandChart
    ]);
    const className = props.isComparisonDashboard
        ? styles.nextChart + " " + (props.isOpenShell ? " " : styles.comparisonDashboard)
        : props.isDashboard
            ? styles.nextChartDashboard
            : props.isSmall
                ? styles.nextChartSmall
                : props.isExploreTask
                    ? styles.exploreNextChart
                    : styles.nextChart;
    return (React.createElement("div", { className: className, style: props.style, id: chartId, onMouseMove: () => updateIsHoverMouse(true), onMouseLeave: () => updateIsHoverMouse(false) },
        isHoverMouse === true && props.isExpand !== true && props.isDashboard === true && (React.createElement(ChartNextActions, { reportId: props.id, node: props.node, isExpandChart: props.isExpandChart, updateIsExpandChart: props.updateIsExpandChart, hidePin: props.hidePin })),
        ChartComponent));
};
const NextChartContainer = (props) => {
    const dispatch = useDispatch();
    const possibleId = getId(props);
    const id = possibleId === "new" ? possibleId : Number(possibleId);
    const possibleReportId = props?.reportId ?? props?.id;
    const reportId = possibleReportId === "new" ? possibleReportId : Number(possibleReportId);
    const reportDatasetId = useSelector((state) => nextReducer.getDatasetId(state, reportId));
    const datasetId = props?.datasetId ?? props?.dataset?.id ?? reportDatasetId;
    const chartId = props.chart?.metadata ? JSON.stringify(props.chart.metadata) : undefined;
    const [isLoading, updateIsLoading] = useState(props.isLoading);
    const insight = useSelector((state) => {
        if (!props.chart?.metadata) {
            return props.chart;
        }
        if (props.isTableauMode || props.isChatGPT) {
            if (isInsight(props.chart)) {
                return props.chart;
            }
            else {
                return nextReducer.getInsightFromMetadata(state, id, props.chart.metadata, false, undefined, datasetId);
            }
        }
        else {
            if (props?.useNode === true || isInsight(props.chart)) {
                return props.chart;
            }
            else {
                return nextReducer.getInsightFromMetadata(state, reportId, props.chart.metadata, false, undefined, datasetId);
            }
        }
    }, (l, r) => {
        return (l == r ||
            (l !== undefined &&
                r !== undefined &&
                l.metadata.type == r.metadata.type &&
                l.narrative.title == r.narrative.title &&
                l.chart?.yAxis?.label == r.chart?.yAxis?.label &&
                l.chart.data.length == r.chart.data.length));
    });
    useEffect(() => {
        if (insight === undefined && props.chart !== undefined && !isInsight(props.chart)) {
            updateIsLoading(true);
            dispatch(nextReducer.fetchMissingInsightPivotTables(props.id, props.chart.metadata, datasetId));
        }
        else if (isLoading && insight !== undefined) {
            updateIsLoading(false);
        }
    }, [insight === undefined, chartId]);
    if (insight !== undefined && isInsight(insight)) {
        return React.createElement(NextChartWrapper, { ...props, id: id, insight: insight });
    }
    if (isLoading) {
        return React.createElement(LoadingIndicator, { style: { margin: "1em auto" } });
    }
    return React.createElement("div", null, "Chart Not Found");
};
export default NextChartContainer;
const CustomizedAxisTick = ({ x, y, payload, isHighlight, showAll, summaryValue }) => {
    const isHighlightTick = isHighlight === payload?.index || summaryValue === payload?.value;
    const isHideTick = showAll !== true &&
        payload !== undefined &&
        (isHighlight === payload?.index + 1 || isHighlight === payload?.index - 1);
    const pIndex = payload?.index ?? 0;
    const value = payload?.value ?? "";
    const isLongLabel = value.length > CHART_TICK_LENGTH + 2;
    const retLabel = isLongLabel ? value.substring(0, CHART_TICK_LENGTH) + "..." : value;
    return showAll !== true && pIndex % 2 === 0 && isHighlightTick !== true ? (React.createElement(React.Fragment, null)) : (React.createElement("g", { transform: `translate(${x ?? 0},${y ?? 0})` },
        React.createElement("text", { x: 0, y: 0, dy: 6, dx: -3, opacity: isHideTick === true ? 0.4 : 1, textAnchor: "end", fontWeight: isHighlightTick ? "bold" : undefined, fill: isHighlightTick ? "#512f77" : "#666", transform: "rotate(-45)" }, retLabel)));
};
const CustomTooltip = ({ id, active, payload, isMaxWidth, outcome, comparisonOptions, hideValues, comparisonNarrative, isContinuous, showAll, hideChangeInBehavior, hideChangeInOutcome, hideChangeInPopulation, hideChangeInTotalPopulation }) => {
    const renameOutcome = useSelector((state) => chatGPTReducer.getRenameOutcome(state));
    const renameTotalPopulation = useSelector((state) => chatGPTReducer.getRenameTotalPopulation(state));
    if (active && payload && payload.length) {
        const whenLabel = payload?.[0]?.payload?.label;
        let retPayload = payload;
        if (comparisonOptions) {
            retPayload = [...payload].reverse();
        }
        if (comparisonNarrative !== undefined) {
            const getSum = (summary) => {
                if (typeof summary?.getSum === "function") {
                    return summary.getSum();
                }
                return summary?.sum;
            };
            const fPayload = payload?.[0].payload;
            return (React.createElement("div", { style: {
                    backgroundColor: "#FFF",
                    color: "#333333",
                    border: "1px solid #00000050",
                    padding: "10px",
                    maxWidth: isMaxWidth ? "75vw" : "450px"
                } },
                React.createElement("p", { style: { marginBottom: "40px" } },
                    React.createElement("strong", null, `${payload[0]?.payload?.name}`),
                    whenLabel && (React.createElement("span", null,
                        React.createElement("span", null, ": "),
                        React.createElement("strong", null, whenLabel)))),
                retPayload.map((record, i) => {
                    const isOtherY = record.dataKey === "otherY";
                    const color = isOtherY ? "#B5B4D0" : record.fill;
                    const label = record?.name;
                    const subgroup = record?.payload?.label;
                    const value = hideChangeInBehavior === true && record?.dataKey === "summary.sum"
                        ? record.value
                        : hideChangeInPopulation === true && record?.dataKey === "yCount"
                            ? record.value
                            : `${isNaN(Number(record.value)) ? "N/A" : record.value}%`;
                    const countValue = isOtherY ? record.payload?.yCount : getSum(record.payload);
                    const countLabel = isOtherY ? `Count when ${subgroup}` : `Count when ${subgroup}`;
                    const totalCountValue = isOtherY
                        ? record.payload?.otherYCount
                        : getSum(record.payload?.summary) + getSum(record.payload?.otherSummary);
                    let icon = (React.createElement("div", { style: {
                            width: "20px",
                            height: "20px",
                            borderRadius: "10px",
                            background: color,
                            marginRight: "5px"
                        } }));
                    const isPreviousTimePeriod = comparisonOptions
                        ? nextReducer.getIsReferenceDate(undefined, undefined, payload[0]?.payload?.name, comparisonOptions)
                        : false;
                    let averages;
                    if (isGroupedNarrative(comparisonNarrative)) {
                        const sMetric = comparisonNarrative?.metrics?.find((m) => m.series?.label === label);
                        const isHidden = checkIsHidden(label, sMetric?.rank, hideValues);
                        if (isHidden && !showAll) {
                            return React.createElement(React.Fragment, null);
                        }
                        if (isOtherY) {
                            averages = sMetric?.metrics?.[isPreviousTimePeriod ? "previousCountPercentage" : "newCountPercentage"];
                        }
                        else {
                            averages = sMetric?.metrics?.[isPreviousTimePeriod ? "previousAdjustedWinRate" : "newAdjustedWinRate"];
                        }
                        const iconColor = modifyRGBValues(record.fill, { dataKey: record.dataKey }, comparisonNarrative, hideValues);
                        icon = (React.createElement("div", { style: {
                                width: "20px",
                                height: "20px",
                                borderRadius: "10px",
                                background: iconColor,
                                marginRight: "5px"
                            } }));
                        // icon = <span style={{marginRight: "4px"}}>
                        //   <CustomizedDot
                        //     sIndex={record?.id ?? i}
                        //     isOutline
                        //     cWidth={20}
                        //     cHeight={20}
                        //     cStroke={color}
                        //     isCustomTooltip
                        //   />
                        // </span>
                    }
                    else {
                        if (isOtherY) {
                            averages =
                                comparisonNarrative?.metrics?.[isPreviousTimePeriod ? "previousCountPercentage" : "newCountPercentage"];
                        }
                        else {
                            averages =
                                comparisonNarrative?.metrics?.[isPreviousTimePeriod ? "previousAdjustedWinRate" : "newAdjustedWinRate"];
                        }
                        if (hideChangeInBehavior === true && !isOtherY) {
                            if (isPreviousTimePeriod) {
                                averages = (comparisonNarrative?.metrics?.previousWins /
                                    (comparisonNarrative?.metrics?.referenceDateBuckets ?? 1)).toFixed(2);
                            }
                            else {
                                averages = (comparisonNarrative?.metrics?.newWins / (comparisonNarrative?.metrics?.currentDateBuckets ?? 1)).toFixed(2);
                            }
                        }
                        if (hideChangeInPopulation === true && record?.dataKey === "yCount") {
                            if (isPreviousTimePeriod) {
                                averages = (comparisonNarrative?.metrics?.previousPursued /
                                    (comparisonNarrative?.metrics?.referenceDateBuckets ?? 1)).toFixed(2);
                            }
                            else {
                                averages = (comparisonNarrative?.metrics?.newPursued / (comparisonNarrative?.metrics?.currentDateBuckets ?? 1)).toFixed(2);
                            }
                        }
                    }
                    return (React.createElement("div", { key: i, className: styles.tooltipItem },
                        React.createElement("div", { style: {
                                marginTop: "5px",
                                display: "flex",
                                alignItems: "center"
                            } },
                            icon,
                            React.createElement("div", { style: { textAlign: "left" } },
                                React.createElement("strong", null, label),
                                React.createElement("strong", null, ":"),
                                "\u00A0"),
                            React.createElement("div", { style: { minWidth: "50px", marginLeft: "5px" } },
                                React.createElement("strong", null, value))),
                        (record.dataKey !== "yCount" || hideChangeInPopulation === true) &&
                            record.dataKey !== "otherYCount" ? (React.createElement(React.Fragment, null,
                            !isNaN(Number(averages)) && (React.createElement("div", { style: {
                                    marginTop: "10px",
                                    marginBottom: "5px",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center"
                                } },
                                React.createElement("span", { style: { textAlign: "left" } },
                                    `Average from ${isPreviousTimePeriod
                                        ? `${comparisonOptions?.referenceStart} to ${comparisonOptions?.referenceEnd}`
                                        : `${comparisonOptions?.currentStart} to ${comparisonOptions?.currentEnd}`}:`,
                                    "\u00A0"),
                                React.createElement("span", { style: { minWidth: "50px", marginLeft: "5px" } },
                                    React.createElement("strong", null, averages + (hideChangeInBehavior && !isOtherY ? "" : "%"))))),
                            hideChangeInOutcome !== true && i === retPayload?.length - 1 && hideChangeInBehavior !== true && (React.createElement("div", { style: {
                                    marginTop: "10px",
                                    display: "flex",
                                    alignItems: "center"
                                } },
                                React.createElement("div", { style: { textAlign: "left" } },
                                    React.createElement("strong", null, "Total " + renameOutcome),
                                    React.createElement("strong", null, ":"),
                                    "\u00A0"),
                                React.createElement("div", { style: { minWidth: "50px", marginLeft: "5px" } },
                                    React.createElement("strong", null, fPayload?.summary?.sum)))),
                            countValue !== undefined &&
                                record.dataKey !== "yCount" &&
                                record.dataKey !== "otherYCount" &&
                                hideChangeInTotalPopulation !== true && (React.createElement("div", { style: {
                                    marginTop: "10px",
                                    marginBottom: "0px",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center"
                                } },
                                React.createElement("span", { style: { textAlign: "left" } },
                                    `${countLabel}:`,
                                    "\u00A0"),
                                React.createElement("span", { style: { minWidth: "50px", marginLeft: "5px" } },
                                    React.createElement("strong", null, countValue)))),
                            countValue !== undefined &&
                                record.dataKey !== "yCount" &&
                                record.dataKey !== "otherYCount" &&
                                hideChangeInTotalPopulation !== true && (React.createElement("div", { style: {
                                    marginTop: "5px",
                                    marginBottom: "5px",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center"
                                } },
                                React.createElement("span", { style: { textAlign: "left", fontWeight: "bold" } },
                                    renameTotalPopulation + ": ",
                                    "\u00A0"),
                                React.createElement("span", { style: { minWidth: "50px", marginLeft: "5px" } },
                                    React.createElement("strong", null, totalCountValue)))))) : (React.createElement(React.Fragment, null))));
                })));
        }
        if (isContinuous) {
            return (React.createElement("div", { style: {
                    backgroundColor: "#FFF",
                    color: "#333333",
                    border: "1px solid #00000050",
                    padding: "10px",
                    maxWidth: isMaxWidth ? "75vw" : "450px"
                } },
                React.createElement("p", { style: { marginBottom: "40px" } },
                    React.createElement("strong", null, `${comparisonOptions ? payload[0]?.payload?.name : payload[0].name}`),
                    whenLabel && !comparisonOptions && (React.createElement("span", null,
                        React.createElement("span", null, ": "),
                        React.createElement("strong", null, whenLabel)))),
                payload.map((record, i) => {
                    const rPayload = record.payload;
                    let count = rPayload?.summary?.getTotalN() ?? rPayload?.yCount ?? rPayload?.summary?.n;
                    let summary = rPayload?.summary;
                    let label = rPayload?.label ?? rPayload?.name;
                    let value = record.value;
                    if (record.dataKey === "otherYTotal") {
                        value = rPayload.otherY;
                    }
                    if (record.dataKey === "yTotal") {
                        value = rPayload.y;
                    }
                    if (record.dataKey === "otherY" || record.dataKey === "otherYTotal") {
                        count = rPayload?.otherSummary?.getTotalN() ?? rPayload?.otherYCount;
                        summary = rPayload?.otherSummary;
                        label = rPayload?.otherLabel;
                    }
                    return (React.createElement("div", { key: i, className: styles.tooltipItem },
                        React.createElement("div", { style: {
                                marginTop: "5px",
                                display: "flex",
                                alignItems: "center"
                            } },
                            React.createElement("div", { style: {
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "10px",
                                    background: record.fill,
                                    marginRight: "5px",
                                    flexShrink: 0
                                } }),
                            React.createElement("div", { style: { textAlign: "left" } },
                                `${record.dataKey == "otherY" || record.dataKey === "otherYTotal"
                                    ? rPayload?.otherLabel
                                    : (rPayload?.label ?? rPayload?.name)}: `,
                                "\u00A0"),
                            React.createElement("div", { style: { minWidth: "50px", marginLeft: "5px" } },
                                React.createElement("strong", null, `${isNaN(Number(value)) ? "N/A" : numeral(value).format("0,0.[00]")}${record.unit ?? ""}`))),
                        summary !== undefined && (React.createElement("div", { style: {
                                marginTop: "10px",
                                marginBottom: "5px",
                                width: "100%",
                                display: "flex",
                                alignItems: "center"
                            } },
                            React.createElement("div", { style: { textAlign: "left" } },
                                `Total of ${record.name} when ${label}:`,
                                "\u00A0"),
                            React.createElement("div", { style: { minWidth: "50px", marginLeft: "5px", flexShrink: "0" } },
                                React.createElement("strong", null, Math.abs(summary.sum) > 1 ? numeral(summary.sum).format("0,0.[00]") : summary.sum)))),
                        count !== undefined && (React.createElement("div", { style: {
                                marginTop: "10px",
                                marginBottom: "5px",
                                width: "100%",
                                display: "flex",
                                alignItems: "center"
                            } },
                            React.createElement("div", { style: { textAlign: "left" } },
                                `Total Count of ${label}:`,
                                "\u00A0"),
                            React.createElement("div", { style: { minWidth: "50px", marginLeft: "5px", flexShrink: "0" } },
                                React.createElement("strong", null, numeral(count).format("0,0")))))));
                })));
        }
        return (React.createElement("div", { style: {
                backgroundColor: "#FFF",
                color: "#333333",
                border: "1px solid #00000050",
                padding: "10px",
                maxWidth: isMaxWidth ? "75vw" : "450px"
            } },
            React.createElement("p", { style: { marginBottom: "40px" } },
                React.createElement("strong", null, `${comparisonOptions ? payload[0]?.payload?.name : payload[0].name}`),
                whenLabel && !comparisonOptions && (React.createElement("span", null,
                    React.createElement("span", null, ": "),
                    React.createElement("strong", null, whenLabel)))),
            payload.map((record, i) => (React.createElement("div", { key: i, className: styles.tooltipItem },
                React.createElement("div", { style: {
                        marginTop: "5px",
                        display: "flex",
                        alignItems: "center"
                    } },
                    React.createElement("div", { style: {
                            width: "20px",
                            height: "20px",
                            borderRadius: "10px",
                            background: record.fill,
                            marginRight: "5px",
                            flexShrink: 0
                        } }),
                    React.createElement("div", { style: { textAlign: "left" } },
                        `${record.dataKey == "otherY" ? record.payload.otherLabel : (record.payload.label ?? record.payload.name)}: `,
                        "\u00A0"),
                    React.createElement("div", { style: { minWidth: "50px", marginLeft: "5px" } },
                        React.createElement("strong", null, `${isNaN(Number(record.value)) ? "N/A" : record.value}${record.unit ?? ""}`))),
                record.dataKey !== "yCount" && record.dataKey !== "otherYCount" ? (React.createElement(React.Fragment, null, record?.payload?.[record.dataKey == "otherY" ? "otherYCount" : "yCount"] !== undefined &&
                    record.dataKey !== "yCount" &&
                    record.dataKey !== "otherYCount" && (React.createElement("div", { style: {
                        marginTop: "10px",
                        marginBottom: "5px",
                        width: "100%",
                        display: "flex",
                        alignItems: "center"
                    } },
                    React.createElement("span", { style: { textAlign: "left" } },
                        `Total Count of ${record.dataKey == "otherY"
                            ? record.payload.otherLabel
                            : (record.payload.label ?? record.payload.name)}:`,
                        "\u00A0"),
                    React.createElement("span", { style: { minWidth: "50px", marginLeft: "5px" } },
                        React.createElement("strong", null, record.payload?.[record.dataKey == "otherY" ? "otherYCount" : "yCount"])))))) : (React.createElement("div", { style: {
                        marginTop: "10px",
                        marginBottom: "5px",
                        width: "100%",
                        display: "flex",
                        alignItems: "center"
                    } },
                    React.createElement("span", { style: { textAlign: "left" } },
                        `${outcome}:`,
                        "\u00A0"),
                    React.createElement("span", { style: { minWidth: "50px", marginLeft: "5px" } },
                        React.createElement("strong", null, record.payload?.[record.dataKey] + "%"))))))),
            payload.length === 1 && (React.createElement(React.Fragment, null,
                React.createElement("div", { style: {
                        marginTop: "10px",
                        marginBottom: "5px",
                        width: "100%",
                        display: "flex",
                        alignItems: "center"
                    } },
                    React.createElement("span", { style: { textAlign: "left" } },
                        `Total Count of ${payload[0]?.name} when ${payload[0]?.payload?.label ?? payload[0]?.payload?.name}:`,
                        "\u00A0"),
                    React.createElement("span", { style: { minWidth: "50px", marginLeft: "5px" } },
                        React.createElement("strong", null, payload[0].payload?.summary?.["sum"]))),
                React.createElement("div", { style: {
                        marginTop: "10px",
                        marginBottom: "5px",
                        width: "100%",
                        display: "flex",
                        alignItems: "center"
                    } },
                    React.createElement("span", { style: { textAlign: "left" } },
                        `Total Count of ${payload[0]?.payload?.label ?? payload[0]?.payload?.name}:`,
                        "\u00A0"),
                    React.createElement("span", { style: { minWidth: "50px", marginLeft: "5px" } },
                        React.createElement("strong", null, payload[0].payload?.summary?.["n"])))))));
    }
    return null;
};
// Custom Y axis labeling with a colored circle for legend, centering the whole item vertically from center
// So that 2 axis charts have visually centered labels + colored legend.
const CustomComparisonYAxisLabel = ({ viewBox, labelText, legendColor = "#B5B4D0", rotation = -90, position = "left", fontSize = 14 }) => {
    const { x, y, height, width } = viewBox ?? {
        x: 0,
        y: 0,
        height: 0,
        width: 0
    };
    const textRef = useRef(null);
    const [textWidth, updateTextWidth] = useState(0);
    const centerY = y + height / 2;
    const isRight = position === "right";
    const offsetX = isRight ? x + width + 2 : x - 2;
    useEffect(() => {
        if (textRef.current) {
            const boundingBox = textRef.current.getBBox();
            updateTextWidth(boundingBox.width);
        }
    });
    const circleRadius = 8;
    const spacing = 8;
    const totalWidth = circleRadius * 2 + spacing + textWidth;
    const offsetY = centerY + totalWidth / 2;
    return (React.createElement("g", { transform: `translate(${offsetX}, ${offsetY})` },
        React.createElement("g", { transform: `rotate(${rotation})` },
            React.createElement("circle", { cx: circleRadius, cy: 0, r: circleRadius, fill: legendColor }),
            React.createElement("text", { ref: textRef, x: circleRadius * 2 + spacing, y: 4, fill: "#333", fontSize: fontSize }, labelText))));
};
